
import SignupReviewDialog from "@/components/SignupReviewDialog.vue";
import Vue from "vue";
import MembershipStatusHistory from "@/components/MembershipStatusHistory/MembershipStatusHistory.vue";
import EducationCredentialsDialog from "@/components/EducationCredentialsDialog/EducationCredentialsDialog.vue";
import { EducationTalent, credentialFieldLabel, credentialDisplay } from "@/helpers/educationCredentials";
import {
  fetchWithAuth,
  getPotentialDuplicates,
  resolveDuplicate,
  getHygienistJobs,
  getHygienistInvoices,
  getHygienistFeedback,
  getHygienistFeedbackCount,
  getHygienistJobRatings,
  updateFeedbackRating,
  updateJobRating,
  getHygienistNotesDetailed,
  getHygienistReliabilityCancellations,
  saveHygienistReliabilityCancellations,
  getTalentOfficeCancellations,
  updateTalentOfficeCancellation,
  createAdminSourceCorrectionIdempotencyKey,
  updateTalentBlockedUntil,
  getHygienistProfile,
  getHygienistStats,
  getUserByHygienist,
  addBlocked,
  getPaginateItems,
  totalItemsByTable,
  updateTable,
  filterDays,
  updateFirstBookedStatus,
  updateCancelledStatuses,
  updateHygienistNotes,
  setHygienistNotes,
  setFieldToNULL,
  updateCourseDisputeAnalyzer,
  applyManualBreak,
  applyManualNotAFit,
  undoManualNotAFit,
  updateHygienist,
  updateHygienistAbout,
  updateHygienistAdminPreference,
  getHygienistQualificationOptions,
  updateHygienistQualificationLevel,
  getHygienistCourses,
  getCurrentAdminRole,
  resetHygienistCourseProgress,
  fetchFfsReferenceSheet,
  getAdminStripeAccountStatus,
  createAdminStripeRequestInformationLink
} from "@/api";
import type {
  AdminStripeAccountSummary,
  CancellationReliabilityAdjustment,
  FairFreshStart,
  HygienistStats,
  HygienistCourse,
  TalentOfficeCancellation
} from "@/api";
import {
  canResetCourseProgress as canResetCourseProgressForRole,
  clearCourseSelectionForTalentSwitch,
  formatCourseCompletionDate as formatCourseCompletionDateForDisplay,
  isCurrentCourseTalent,
  loadCoursesForSelectedTalent
} from "@/helpers/courseProgress";
import {
  beginStripeRequest,
  finishStripeRequest,
  invalidateStripeStatusRequest,
  invalidateStripeRequest,
  isCurrentStripeRequest,
  loadCurrentStripeStatus
} from "@/utils/stripeRequestOrdering";

const OFFICE_CANCELLATION_REASON_OPTIONS = [
  { value: "permanentStaffCovered", text: "Permanent Staff Covered" },
  { value: "patientsCancelled", text: "Patients Cancelled" },
  { value: "doubleBooked", text: "Double-booked with another temp" },
  { value: "badExperiende", text: "Previous bad experience" },
  { value: "tempNoShowed", text: "Temp No-Showed" },
  { value: "other", text: "Other" },
  {
    value: "onBehalfOfTalent",
    text: "Talent asked the office to cancel on their behalf"
  }
];
import { getHeaders, formatDates } from "@/helpers";
import {
  blockedUntilRetryState,
  localBlockedUntilToUtc,
  saveAndRefreshBlockedUntil
} from "@/helpers/blockedUntilEditor";
import {
  formatJobStatus,
  JOB_STATUS_COLORS
} from "@/helpers/adminEnumLabels";
import { normalizeFirstShiftBookedRows } from "@/helpers/firstShiftBookedRows";
import {
  getFfsStatusColor,
  getFfsStatusLabel,
  getFfsStatusIcon,
  ffsStatusNeedsDarkText,
  recoveryOverlayToDisplayCode,
  FFS_STATUS_COLOR_KEY
} from "@/helpers/ffsStatus";
import { getTalentPresentationRowClass } from "@/helpers/talentPresentation";
import { shouldShowCancellationsOverviewSaveHelper } from "@/helpers/cancellationsOverviewSave";
import {
  buildMembershipSnapshot,
  canUndoManualNotAFit as isManualNotAFitUndoAllowed,
  correctionResultMessage,
  refreshCorrectionViews,
  refreshRatingCorrectionViews,
  reconcileMembershipAfterCorrection as reconcileCorrectionMembership,
  actionableAdminActionError,
  runManualBreakAction,
  runMemberReviewNotAFitAction,
  runManualNotAFitAction,
  runRatingSourceCorrectionAction,
  MembershipSnapshot,
  MembershipReconciliationOutcome
} from "@/helpers/membershipReconciliation";
import { constants } from "@/utils/constants";
import { startCase } from "lodash";
import { getNewMemberReviewReasonCategory } from "@/helpers/membershipReasonCategory";
import { paginateTable } from "@/interfaces/Additional.interfaces";
import UpdateMultipleItems from "@/components/Tables/components/UpdateMultipleItems/UpdateMultipleItems.vue";
import PhoneInfoModal from "@/components/Tables/components/PhoneInfoModal/PhoneInfoModal.vue";
import ShiftsInDialogMemberReview from "@/components/Tables/MemberReview/components/ShiftsInDialog/ShiftsInDialog.vue";
import ShiftsInDialogFirstShiftBooked from "@/components/Tables/FirstShiftBooked/components/ShiftsInDialog/ShiftsInDialog.vue";
import ColumnManager from "@/components/shared/ColumnManager.vue";
import EntityIdLink from "@/components/shared/EntityIdLink";
import BlocksFavourites from "@/components/shared/BlocksFavourites";
import FirstShiftFaqDialog from "@/components/FirstShiftFaqDialog/FirstShiftFaqDialog.vue";
import StripeAccountStatusPanel from "@/components/shared/StripeAccountStatusPanel.vue";
import SignupStatusChip from "@/components/shared/SignupStatusChip.vue";

const DEFAULT_PROFILE_PHOTO =
  "https://via.placeholder.com/120x120?text=No+Photo";

interface TalentMembershipOverlay {
  active: boolean;
  enforced: boolean;
  source: "valueGives" | "ffs" | "support_manual";
  sourceSystem?: string | null;
  state: "course_required" | "restricted_reentry" | "on_a_break" | "not_a_fit";
  recoveryMechanism?: string | null;
  reasonCategory?:
    | "credential_review"
    | "administrative_block"
    | "legacy_import"
    | "system_outcome"
    | "unknown";
  canEditManualBreak?: boolean;
  onABreakUntil?: string | null;
  canUndoManualNotAFit?: boolean;
}

interface TalentMembershipStatus {
  currentStatusCode: string | null;
  displayLabel: string;
  overlay: TalentMembershipOverlay | null;
}

interface Hygienist {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  type: number;
  createdOn: string;
  city: string;
  province: string;
  postalCode: string;
  country: number;
  graduationYear: number;
  firstLicensedYear?: number | null;
  firstLicensedMonth?: number | null;
  educationCredentialsConfirmedOn?: string | null;
  about: string;
  status: number;
  // T2-6362: FFS string status code joined from MembershipManagement.
  currentStatusCode?: string | null;
  lat: string;
  lon: string;
  stripeAccountId: string | null;
  stripeCreatedTimestamp?: string | null;
  isOrtho: number;
  isRestorative: number;
  isComplete?: number | string | boolean | null;
  isDupe: string | null;
  cancellationScore: number;
  completedJobs?: number;
  school?: string;
  CDHONumber?: string;
  address?: string;
  blockedUntil?: string | null;
  etransferEmail?: string;
  photoUrl?: string | null;
  Notes?: string | null;
  accountStatus?: number | null;
  userId?: number | null;
  valueGives?: number | null;
  Verified?: number | null;
  membership?: TalentMembershipStatus | null;
}

interface Job {
  id: number;
  status: number;
  startDate: string;
  locationId: number;
  hygienistId: number;
  dentistRating: number;
  hygienistRating: number;
  dentistBilled: number;
  hourlyRate: number;
  postedOn: string;
  type: number;
  bookedOn: string;
}

interface Invoice {
  id: number;
  jobId: number;
  totalHours: number;
  totalUnpaidHours: number;
  totalBillableHours: number;
  hourlyRate: number;
  totalInvoiceAmt: number;
  createdOn: string;
}

interface Payment {
  id: number;
  dentistId: number;
  date: string;
  amount: number;
  description: string;
  jobId: number;
}

interface UserRecord {
  id: number;
  email?: string | null;
  hygienistId?: number | string | null;
}

interface WorkExperienceRow {
  id: number;
  jobTitle: string | null;
  companyName: string | null;
  city: string | null;
  current: boolean | number | null;
  employmentType: string | null;
  fromDate: string | null;
  toDate: string | null;
}

interface HygienistProfile {
  id: number;
  isComplete?: number | string | boolean | null;
  status: number;
  // T2-6361: FFS string status code surfaced on the profile payload.
  currentStatusCode?: string | null;
  blockedUntil: string | null;
  type: number;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: number | null;
  lat: string | null;
  lon: string | null;
  about: string | null;
  // T2-6546: Admin/Reception shift preference. NULL = no decision,
  // date > 1970 = opted in, 1970-01-01 = permanently declined.
  agreedAdmin?: string | null;
  Notes?: string | null;
  phone: string | null;
  photoUrl: string | null;
  qualifications: string[];
  skills: string[];
  email?: string | null;
  valueGives?: number | null;
  school?: string | null;
  graduationYear?: number | null;
  firstLicensedYear?: number | null;
  firstLicensedMonth?: number | null;
  educationCredentialsConfirmedOn?: string | null;
  resumeUrl?: string | null;
  hireResumeUrl?: string | null;
  workExperience?: WorkExperienceRow[];
  membership?: TalentMembershipStatus | null;
}

type CancellationAdminMode = "scored" | "ignored" | "grouped";

const TALENT_CANCELLATION_REASON_OPTIONS = [
  { value: "illness", text: "Illness" },
  { value: "caregiver", text: "Caregiver Related" },
  { value: "transportation", text: "Transportation Related" },
  { value: "conflict", text: "Scheduling Conflict" },
  { value: "other", text: "Other" }
];

const TALENT_CANCELLATION_REASON_CODES = new Set(
  TALENT_CANCELLATION_REASON_OPTIONS.map(option => option.value)
);

interface Cancellation {
  id: number;
  jobId: number;
  cancelTimestamp: string;
  rebookedBy: number;
  hygienistId: number;
  reason: string;
  details: string;
  isGracePeriodCancellation: number;
  shiftDate?: string;
  firstName?: string;
  lastName?: string;
  practiceName?: string;
  ifDone?: number;
  locationId?: number | null;
  cleanSlateTimestamp?: string | null;
  countsTowardBlacklist?: number | boolean;
  countsTowardReliability?: number | boolean;
  groupedWith?: number | null;
  groupedWithJobId?: number | null;
  groupedWithShiftDate?: string | null;
  groupedWithPracticeName?: string | null;
  cancellationScore?: number | string | null;
  isIgnored?: number | boolean;
  adminMode?: CancellationAdminMode;
  originalAdminMode?: CancellationAdminMode;
  adminGroupedWith?: number | null;
  originalGroupedWith?: number | null;
  adminCancellationScore?: number | string | null;
  originalCancellationScore?: number | null;
  adminCancellationScoreEdited?: boolean;
  originalReason?: string;
  originalDetails?: string;
}

interface Note {
  id: number;
  note: string;
  createdOn: string;
}

interface Duplicate {
  duplicateHygienistId: number;
  firstName: string;
  lastName: string;
  address: string;
  postalCode: string;
}

interface TableHeader {
  text: string;
  value: string;
  sortable?: boolean;
  align?: string;
}

interface DataTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy?: string[];
  sortDesc?: boolean[];
  groupBy?: string[];
  groupDesc?: boolean[];
  multiSort?: boolean;
  mustSort?: boolean;
}

interface ComponentData {
  educationDialog: boolean;
  educationTalent: EducationTalent | null;
  educationRequestId: number;
  currentTab: number;
  countDays: number;
  searchQuery: string;
  selectedHygienist: Hygienist | null;
  hygienists: Hygienist[];
  loadingHygienists: boolean;
  // T2-6449: loading state for the FFS Reference Sheet button
  ffsReferenceLoading: boolean;
  firstShiftFaqDialog: boolean;
  stripeAccountStatus: AdminStripeAccountSummary | null;
  stripeStatusLoading: boolean;
  stripeStatusRequestId: number;
  stripeStatusError: string;
  stripeRequestLoading: boolean;
  stripeRequestId: number;
  stripeRequestLink: string;
  // T2-6229: About Me admin override
  editingAbout: boolean;
  aboutDraft: string;
  aboutError: string;
  savingAbout: boolean;
  // T2-6546: Admin/Reception shift preference controls
  savingAdminPreference: "" | "optIn" | "optOut" | "reset";
  adminPreferenceDialog: { open: boolean; preference: "" | "optIn" | "optOut" | "reset" };
  totalHygienists: number;
  tableOptions: DataTableOptions;
  searchDebounceTimer: any;
  isFetchingFromSearch: boolean;
  managedTalentHeaders: TableHeader[];
  jobs: Job[];
  invoices: Invoice[];
  payments: Payment[];
  cancellations: Cancellation[];
  ignoredCancellations: Cancellation[];
  savingCancellationReliability: boolean;
  cancellationReliabilityNote: string;
  cancellationCorrectionReason: string;
  cancellationEvidenceReference: string;
  cancellationCorrectionIdempotencyKey: string;
  // T2-6367: server-sourced FFS weighted reliability shown in the editor header.
  cancellationWeightedReliability: number | null;
  notes: Note[];
  duplicates: Duplicate[];
  feedback: any[];
  feedbackFullTextDialog: boolean;
  feedbackFullTextContent: any;
  feedbackTotal: number;
  loadingFeedback: boolean;
  feedbackAbortController: AbortController | null;
  feedbackOptions: any;
  feedbackHeaders: any[];
  feedbackCommentFields: any[];
  // T2-6478: Job Rating section
  jobRatings: any[];
  jobRatingsTotal: number;
  loadingJobRatings: boolean;
  jobRatingsAbortController: AbortController | null;
  jobRatingsOptions: any;
  jobRatingHeaders: any[];
  ratingCorrectionDialog: {
    open: boolean;
    sourceType: "feedback" | "job";
    item: any | null;
    field: string;
    value: number | null;
    reason: string;
    evidenceReference: string;
    idempotencyKey: string;
    submitting: boolean;
  };
  profileInfo: HygienistProfile | null;
  editingAddress: boolean;
  addressSearchText: string;
  addressPredictions: any[];
  addressSaving: boolean;
  editedAddress: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    country: number;
    lat: string;
    lon: string;
  };
  placesService: any;
  autocompleteService: any;
  autocompleteSessionToken: any;
  showMembershipActionsModal: boolean;
  showCancellationsModal: boolean;
  showOfficeCancellationsModal: boolean;
  loadingOfficeCancellations: boolean;
  officeCancellations: TalentOfficeCancellation[];
  officeCancellationReasonOptions: Array<{ text: string; value: string }>;
  officeCancellationsHeaders: Array<{ text: string; value: string; width?: string; sortable?: boolean }>;
  officeCancellationEditDialog: {
    open: boolean;
    item: TalentOfficeCancellation | null;
    reason: string;
    details: string;
    originalReason: string;
    onBehalfAcknowledged: boolean;
    supportNote: string;
    noteError: string;
    saving: boolean;
  };
  blockOfficeDialog: boolean;
  selectedMembershipAction: string;
  membershipActionIdempotencyKey: string;
  membershipActions: string[];
  manualBreakForm: {
    durationMonths: number | null;
    note: string;
    evidenceReference: string;
  };
  manualNotFitForm: {
    note: string;
    ticketReference: string;
  };
  isApplyingMembershipAction: boolean;
  officeIdToBlock: number | null;
  blockedUntilPrevValues: Record<number, string | null>;
  blockedUntilEditor: {
    show: boolean;
    hygienist: Hygienist | null;
    dateTimeLocal: string;
    reason: string;
    ticketReference: string;
    saving: boolean;
    idempotencyKey: string;
    requestPayload: string;
  };
  snackbar: {
    show: boolean;
    text: string;
    color: string;
  };
  notesDialog: {
    show: boolean;
    content: string;
    editContent: string;
    isEditing: boolean;
    isSaving: boolean;
    hygienistId: number | null;
  };
  unsavedChangesDialog: {
    show: boolean;
    action: "cancel" | "close" | null;
  };
  showFirstShiftBookedModal: boolean;
  firstShiftBookedModalHeader: string;
  firstShiftBookedModalReason: string;
  firstShiftBookedBtnSubmit: string;
  firstShiftBookedBtnCancel: string;
  firstShiftBookedModalData: {
    JobId: number;
    HygienistId: number;
    firstName: string;
    email: string;
  };
  firstShiftBookedStatus: string | number;
  firstShiftBookedTempData: any;
  showSameCancelled: boolean;
  showMemberReviewModal: boolean;
  memberReviewModalHeader: string;
  memberReviewModalReason: string;
  // T2-6429: Hold/Block on the New Members Review tab now route to the FFS
  // "Not a Fit" flow, which requires a support note AND a ticket reference.
  memberReviewModalTicket: string;
  memberReviewModalAction: "hold" | "block" | "";
  memberReviewModalSubmitting: boolean;
  memberReviewModalIdempotencyKey: string;
  memberReviewModalSubmit: () => void;
  memberReviewModalData: {
    id: number;
    firstName: string;
    email: string;
    status: number;
  };
  memberReviewStatusChange: boolean;
  currentHygienistStatus: string;
  hygienistTypeCode: Record<number, string>;
  hygienistHeaders: TableHeader[];
  jobsHeaders: TableHeader[];
  invoicesHeaders: TableHeader[];
  paymentsHeaders: TableHeader[];
  cancellationsHeaders: TableHeader[];
  ignoredCancellationsHeaders: TableHeader[];
  duplicatesHeaders: TableHeader[];
  resolvingDuplicateId: number | null;
  // Clear Blocked Until Data
  // Show Stats Dialog
  showStatsDialog: boolean;
  loadingStats: boolean;
  hygienistStats: HygienistStats | null;
  // Edit Max Booked Dialog
  // Edit Value Gives Dialog
  // School edit (merged from former Expanded Details — T2-6228)
  editableSchool: string;
  // T2-6513 / T2-6706: editable DA Level / Designation (Qualifications section)
  editingDaLevel: boolean;
  loadingDaLevelOptions: boolean;
  savingDaLevel: boolean;
  editableDaLevelSkillId: number | null;
  daLevelCurrentSkillId: number | null;
  daLevelOptions: Array<{ id: number; name: string; label: string }>;
  daLevelEditError: string;
  // T2-7535: designations saved together (e.g. ["CDA", "DA"]); empty when there is one.
  daLevelConflictNames: string[];
  // Collapsible sections state
  collapsedSections: {
    notes: boolean;
    jobs: boolean;
    invoices: boolean;
    duplicates: boolean;
    payments: boolean;
    feedback: boolean;
    jobRatings: boolean;
    courses: boolean;
    blocksFavourites: boolean;
  };
  // T2-6397: Courses section state.
  courses: HygienistCourse[];
  loadingCourses: boolean;
  courseRequestId: number;
  courseResetRequestId: number;
  currentAdminRole: number | null;
  loadingCourseResetPermission: boolean;
  resettingCourseCode: string;
  courseResetDialog: {
    open: boolean;
    course: HygienistCourse | null;
    hygienistId: number | null;
  };
}

function isFetchResponse(value: unknown): value is Response {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Response).json === "function"
  );
}

async function parseJsonResponse<T>(
  response: Response | T | null,
  fallback: T
): Promise<T> {
  if (!response) {
    return fallback;
  }

  if (isFetchResponse(response)) {
    try {
      if (!response.ok) {
        return fallback;
      }
      return (await response.json()) as T;
    } catch {
      return fallback;
    }
  }

  return response as T;
}

export default Vue.extend<ComponentData, any, any, any>({
  name: "TalentConsolidated",
  components: {
    MembershipStatusHistory,
    SignupReviewDialog,
    EducationCredentialsDialog,
    UpdateMultipleItems,
    PhoneInfoModal,
    ShiftsInDialogMemberReview,
    ShiftsInDialogFirstShiftBooked,
    ColumnManager,
    EntityIdLink,
    BlocksFavourites,
    FirstShiftFaqDialog,
    StripeAccountStatusPanel,
    SignupStatusChip
  },
  data(): ComponentData {
    return {
      educationDialog: false,
      educationTalent: null,
      educationRequestId: 0,
      currentTab: 2, // 0 = New Members Review, 1 = First Shift Booked, 2 = All
      countDays: 3, // Default days for MemberReview and FirstShiftBooked filters
      searchQuery: "",
      selectedHygienist: null,
      editingAbout: false,
      aboutDraft: "",
      aboutError: "",
      savingAbout: false,
      // T2-6546: Admin/Reception shift preference controls
      savingAdminPreference: "",
      adminPreferenceDialog: { open: false, preference: "" },
      hygienists: [],
      loadingHygienists: false,
      ffsReferenceLoading: false,
      firstShiftFaqDialog: false,
      stripeAccountStatus: null,
      stripeStatusLoading: false,
      stripeStatusRequestId: 0,
      stripeStatusError: "",
      stripeRequestLoading: false,
      stripeRequestId: 0,
      stripeRequestLink: "",
      totalHygienists: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 15
      },
      searchDebounceTimer: null as any,
      isFetchingFromSearch: false,
      managedTalentHeaders: [] as TableHeader[],
      jobs: [],
      invoices: [],
      payments: [],
      cancellations: [],
      ignoredCancellations: [],
      savingCancellationReliability: false,
      cancellationReliabilityNote: "",
      cancellationCorrectionReason: "",
      cancellationEvidenceReference: "",
      cancellationCorrectionIdempotencyKey: "",
      cancellationWeightedReliability: null as number | null,
      notes: [],
      duplicates: [],
      resolvingDuplicateId: null,
      feedback: [],
      feedbackFullTextDialog: false,
      feedbackFullTextContent: "" as any,
      feedbackTotal: 0,
      loadingFeedback: false,
      feedbackAbortController: null as AbortController | null,
      feedbackOptions: { page: 1, itemsPerPage: 5, sortBy: ['shiftDate'], sortDesc: [true] } as any,
      feedbackHeaders: [
        { text: "Job ID",       value: "jobId",                  width: "75px",  sortable: true },
        { text: "Loc ID",       value: "locationId",             width: "70px",  sortable: false },
        { text: "Office",       value: "practiceName",           width: "140px", sortable: false },
        { text: "Shift Date",   value: "shiftDate",              width: "100px", sortable: true },
        { text: "Clinical",     value: "clinicalSkillsRating",   width: "80px",  sortable: true },
        { text: "Profess.",     value: "professionalismRating",  width: "80px",  sortable: true },
        { text: "Commun.",      value: "communicationRating",    width: "80px",  sortable: true },
        { text: "Time Mgmt",    value: "timeManagementRating",   width: "85px",  sortable: true },
        { text: "Rate Sat.",    value: "rateSatisfactionRating", width: "80px",  sortable: true },
        { text: "Comments",     value: "comments",               sortable: false },
        // T2-5698: Private notes per-shift — labeled so support can tell who wrote them.
        { text: "Office Private Notes", value: "officePrivateNotes", width: "180px", sortable: false },
        { text: "Talent Private Notes", value: "talentPrivateNotes", width: "180px", sortable: false }
      ],
      // T2-6478: Job Rating section state (lazy-loaded; newest-first by Start Date).
      jobRatings: [] as any[],
      jobRatingsTotal: 0,
      loadingJobRatings: false,
      jobRatingsAbortController: null as AbortController | null,
      jobRatingsOptions: { page: 1, itemsPerPage: 5 } as any,
      jobRatingHeaders: [
        { text: "Job ID",            value: "jobId",            width: "90px",  sortable: false },
        { text: "Start Date",        value: "startDate",        width: "120px", sortable: false },
        { text: "Hygienist Rating",  value: "hygienistRating",  width: "130px", sortable: false },
        { text: "Dentist Rating",    value: "dentistRating",    width: "130px", sortable: false },
      ],
      ratingCorrectionDialog: {
        open: false,
        sourceType: "feedback" as "feedback" | "job",
        item: null as any,
        field: "",
        value: null as number | null,
        reason: "",
        evidenceReference: "",
        idempotencyKey: "",
        submitting: false
      },
      feedbackCommentFields: [
        { key: "clinicalSkillsDetails",    label: "Clinical Skills" },
        { key: "professionalismDetails",   label: "Professionalism" },
        { key: "communicationDetails",     label: "Communication" },
        { key: "timeManagementDetails",    label: "Time Management" },
        { key: "rateSatisfactionDetails",  label: "Rate Satisfaction" },
        { key: "additionalFeedbackDetails", label: "Additional Feedback" },
        // T2-5698: Surface the private notes in the expanded-text dialog too.
        { key: "officePrivateNotes",       label: "Office Private Notes" },
        { key: "talentPrivateNotes",       label: "Talent Private Notes" }
      ],
      profileInfo: null,
      editingAddress: false,
      addressSearchText: '',
      addressPredictions: [] as any[],
      addressSaving: false,
      editedAddress: {
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: 0,
        lat: '',
        lon: '',
      },
      placesService: null as any,
      autocompleteService: null as any,
      autocompleteSessionToken: null as any,
      showMembershipActionsModal: false,
      showCancellationsModal: false,
      showOfficeCancellationsModal: false,
      loadingOfficeCancellations: false,
      officeCancellations: [] as TalentOfficeCancellation[],
      officeCancellationReasonOptions: OFFICE_CANCELLATION_REASON_OPTIONS,
      officeCancellationsHeaders: [
        { text: "ID", value: "id", width: "70px" },
        { text: "Job ID", value: "jobId", width: "80px", sortable: false },
        { text: "Shift Date", value: "shiftDate", width: "110px" },
        { text: "Practice", value: "practiceName", sortable: false },
        { text: "Cancelled", value: "cancelTimestamp", width: "150px" },
        { text: "SNC", value: "shortNotice", width: "70px", sortable: false },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Details", value: "details", sortable: false },
        { text: "", value: "actions", width: "90px", sortable: false }
      ],
      officeCancellationEditDialog: {
        open: false,
        item: null as TalentOfficeCancellation | null,
        reason: "",
        details: "",
        originalReason: "",
        onBehalfAcknowledged: false,
        supportNote: "",
        noteError: "",
        saving: false
      },
      blockOfficeDialog: false,
      selectedMembershipAction: "",
      membershipActionIdempotencyKey: "",
      // T2-6364: "Cancellations Only", "Negative Feedback" and "Quiz Received"
      // were removed — these legacy course-dispute flows are superseded by Fair
      // Fresh Start recovery courses. "Take a Break" (T2-6366) replaces the old
      // fixed "Take a 2 Month Break" and uses the manual-break dialog.
      // T2-6396: "Not a Fit" remains as the controlled support override.
      membershipActions: ["Not a Fit", "Take a Break", "First Response"],
      manualBreakForm: {
        durationMonths: 3,
        note: "",
        evidenceReference: ""
      },
      manualNotFitForm: {
        note: "",
        ticketReference: ""
      },
      isApplyingMembershipAction: false,
      officeIdToBlock: null,
      blockedUntilPrevValues: {},
      blockedUntilEditor: {
        show: false,
        hygienist: null as Hygienist | null,
        dateTimeLocal: "",
        reason: "",
        ticketReference: "",
        saving: false,
        idempotencyKey: "",
        requestPayload: ""
      },
      snackbar: {
        show: false,
        text: "",
        color: "info"
      },
      notesDialog: {
        show: false,
        content: "",
        editContent: "",
        isEditing: false,
        isSaving: false,
        hygienistId: null
      },
      unsavedChangesDialog: {
        show: false,
        action: null as "cancel" | "close" | null
      },
      // FirstShiftBooked modal data
      showFirstShiftBookedModal: false,
      firstShiftBookedModalHeader: "",
      firstShiftBookedModalReason: "",
      firstShiftBookedBtnSubmit: "Submit",
      firstShiftBookedBtnCancel: "Cancel",
      firstShiftBookedModalData: {
        JobId: 0,
        HygienistId: 0,
        firstName: "",
        email: ""
      },
      firstShiftBookedStatus: "" as string | number,
      firstShiftBookedTempData: undefined as any,
      showSameCancelled: false,
      // MemberReview modal data
      showMemberReviewModal: false,
      memberReviewModalHeader: "",
      memberReviewModalReason: "",
      memberReviewModalTicket: "",
      memberReviewModalAction: "" as "hold" | "block" | "",
      memberReviewModalSubmitting: false,
      memberReviewModalIdempotencyKey: "",
      memberReviewModalSubmit: () => {},
      memberReviewModalData: {
        id: 0,
        firstName: "",
        email: "",
        status: 0
      },
      memberReviewStatusChange: false,
      // Clear Blocked Until Data
      // Show Stats Dialog
      showStatsDialog: false,
      loadingStats: false,
      hygienistStats: null as HygienistStats | null,
      // Edit Max Booked Dialog
      // Edit Value Gives Dialog
      // School edit (merged from former Expanded Details — T2-6228)
      editableSchool: "",
      // T2-6513 / T2-6706: editable DA Level / Designation (Qualifications section)
      editingDaLevel: false,
      loadingDaLevelOptions: false,
      savingDaLevel: false,
      editableDaLevelSkillId: null as number | null,
      daLevelCurrentSkillId: null as number | null,
      daLevelOptions: [] as Array<{ id: number; name: string; label: string }>,
      daLevelEditError: "",
      daLevelConflictNames: [] as string[],
      // Collapsible sections state (default to closed)
      collapsedSections: {
        notes: true,
        jobs: true,
        invoices: true,
        duplicates: true,
        payments: true,
        feedback: true,
        jobRatings: true,
        courses: true,
        blocksFavourites: true
      },
      // T2-6397: Courses section state (lazy-loaded on first expand).
      courses: [] as HygienistCourse[],
      loadingCourses: false,
      courseRequestId: 0,
      courseResetRequestId: 0,
      currentAdminRole: null,
      loadingCourseResetPermission: true,
      resettingCourseCode: "",
      courseResetDialog: {
        open: false,
        course: null as HygienistCourse | null,
        hygienistId: null as number | null
      },

      currentHygienistStatus: "",
      hygienistTypeCode: constants.hygienistType,
      hygienistHeaders: [
        { text: "ID", value: "id", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Sign-up", value: "isComplete", sortable: true },
        { text: "Account Status", value: "accountStatus", sortable: true },
        { text: "Email", value: "email", sortable: true },
        { text: "First Name", value: "firstName", sortable: true },
        { text: "Last Name", value: "lastName", sortable: true },
        { text: "Type", value: "type", sortable: true },
        // T2-5698: Professional designation(s) (RDH, CDA2, …) from Skills.name via TalentSkills,
        // filtered to primary qualifications on the API side. Comma-separated when multiple apply.
        { text: "Level", value: "level", sortable: false },
        { text: "Instabook", value: "InstabookActivated", sortable: true },
        { text: "License #", value: "CDHONumber", sortable: true },
        { text: "City", value: "city", sortable: true },
        { text: "Province", value: "province", sortable: true },
        { text: "Postal Code", value: "postalCode", sortable: true },
        { text: "Grad Year", value: "graduationYear", sortable: true },
        { text: "Registration / Licence", value: "firstLicensedYear", sortable: false },
        { text: "Phone", value: "phone", sortable: true },
        { text: "Address", value: "address", sortable: true },
        { text: "Blocked Until", value: "blockedUntil", sortable: true },
        { text: "eTransfer Email", value: "etransferEmail", sortable: true },
        { text: "School", value: "school", sortable: true },
        { text: "Notes", value: "Notes", sortable: false },
        { text: "Actions", value: "actions", sortable: false }
      ],
      jobsHeaders: [
        { text: "Job ID", value: "id", sortable: true },
        { text: "Location ID", value: "locationId", sortable: true },
        { text: "Location Name", value: "practiceName", sortable: true },
        { text: "Shift Date", value: "startDate", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Hourly Rate", value: "hourlyRate", sortable: true }
      ],
      invoicesHeaders: [
        { text: "Invoice ID", value: "id", sortable: true },
        { text: "Job ID", value: "jobId", sortable: true },
        { text: "Created On", value: "createdOn", sortable: true },
        { text: "Total Amount", value: "totalInvoiceAmt", sortable: true }
      ],
      paymentsHeaders: [
        { text: "Payment ID", value: "id", sortable: true },
        { text: "Date", value: "date", sortable: true },
        { text: "Amount", value: "amount", sortable: true },
        { text: "Description", value: "description", sortable: false }
      ],
      cancellationsHeaders: [
        { text: "Reliability", value: "countsTowardReliability", sortable: true },
        { text: "ID", value: "id", sortable: true },
        { text: "Job ID", value: "jobId", sortable: true },
        { text: "Shift Date", value: "shiftDate", sortable: true },
        { text: "Cancel Timestamp", value: "cancelTimestamp", sortable: true },
        { text: "Practice Name", value: "practiceName", sortable: true },
        { text: "Location ID", value: "locationId", sortable: true },
        { text: "Score", value: "cancellationScore", sortable: true },
        { text: "Mode", value: "adminMode", sortable: false },
        { text: "Grouped With", value: "adminGroupedWith", sortable: false },
        { text: "Grace Period", value: "isGracePeriodCancellation", sortable: true },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Details", value: "details", sortable: false }
      ],
      ignoredCancellationsHeaders: [
        { text: "ID", value: "id", sortable: true },
        { text: "Job ID", value: "jobId", sortable: true },
        { text: "Shift Date", value: "shiftDate", sortable: true },
        { text: "Cancel Timestamp", value: "cancelTimestamp", sortable: true },
        { text: "Hygienist ID", value: "hygienistId", sortable: true },
        { text: "Practice Name", value: "practiceName", sortable: true },
        { text: "Location ID", value: "locationId", sortable: true },
        { text: "Rebooked By", value: "rebookedBy", sortable: true },
        { text: "If Done", value: "ifDone", sortable: true },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Details", value: "details", sortable: false },
        { text: "Actions", value: "actions", sortable: false }
      ],
      duplicatesHeaders: [
        {
          text: "Duplicate Hygienist ID",
          value: "duplicateHygienistId",
          sortable: true
        },
        { text: "First Name", value: "firstName", sortable: true },
        { text: "Last Name", value: "lastName", sortable: true },
        { text: "Postal Code", value: "postalCode", sortable: true },
        { text: "Score", value: "matchScore", sortable: true },
        { text: "Signals", value: "signals", sortable: false },
        { text: "Review Status", value: "status", sortable: true },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    canReviewSelectedSignup(): boolean {
      const selected = (this as any).selectedHygienist;
      return (this as any).currentTab === 0 && Boolean(selected) && Number(selected.reviewed) !== 1;
    },
    // T2-6374: rows for the Status-header colour-key legend popover.
    ffsStatusColorKey(): Array<{
      code: string;
      color: string;
      description: string;
    }> {
      return FFS_STATUS_COLOR_KEY;
    },
    // T2-6362: whether the Status column is the active sort column.
    statusSortActive(): boolean {
      const sortBy = ((this as any).tableOptions || {}).sortBy;
      return Array.isArray(sortBy) && sortBy.indexOf("status") !== -1;
    },
    // T2-6362: current direction of the Status sort (descending = best tier first).
    statusSortDesc(): boolean {
      const sortDesc = ((this as any).tableOptions || {}).sortDesc;
      return Array.isArray(sortDesc) && sortDesc.length
        ? Boolean(sortDesc[0])
        : true;
    },
    accountStatusOptions(): { text: string; value: number }[] {
      return [
        { text: "Deleted", value: -4 },
        { text: "Active", value: 1 }
      ];
    },
    selectedMembership(): TalentMembershipStatus | null {
      return (
        (this as any).profileInfo?.membership ||
        (this as any).selectedHygienist?.membership ||
        null
      );
    },
    selectedMembershipOverlay(): TalentMembershipOverlay | null {
      return (this as any).selectedMembership?.overlay || null;
    },
    isSelectedTalentNotAFit(): boolean {
      return (this as any).selectedMembershipOverlay?.state === "not_a_fit";
    },
    canUndoManualNotAFit(): boolean {
      const overlay = (this as any).selectedMembershipOverlay;
      return isManualNotAFitUndoAllowed(overlay);
    },
    availableMembershipActions(): string[] {
      const baseActions = ((this as any).membershipActions || []).filter(
        (action: string) => {
          const normalized = action.replace(/\s+/g, "").toLowerCase();
          if (normalized === "cleanslate") return false;
          if (normalized === "notafit" && (this as any).isSelectedTalentNotAFit) {
            return false;
          }
          return true;
        }
      );

      if ((this as any).canUndoManualNotAFit) {
        return ["Undo Manual Not a Fit", ...baseActions];
      }
      return baseActions;
    },
    isManualNotFitAction(): boolean {
      return ["Not a Fit", "Undo Manual Not a Fit"].includes(
        (this as any).selectedMembershipAction
      );
    },
    manualNotFitConfirmationCopy(): string {
      return (this as any).selectedMembershipAction === "Undo Manual Not a Fit"
        ? "This removes only the support-applied Not a Fit override. FFS will recalculate the Talent's status from current reliability and feedback data."
        : "This manual support override takes precedence over the system-calculated FFS status until support removes it.";
    },
    isSelectedTalentOnABreak(): boolean {
      return (this as any).selectedMembershipOverlay?.state === "on_a_break";
    },
    isManualBreakAction(): boolean {
      return (this as any).selectedMembershipAction === "Take a Break";
    },
    // T2-6935: Hold/Block is a privileged membership action and must carry
    // both the raw reason and a durable evidence/case reference.
    canSubmitMemberReviewNotAFit(): boolean {
      return !!(
        ((this as any).memberReviewModalReason || "").trim() &&
        ((this as any).memberReviewModalTicket || "").trim()
      );
    },
    manualBreakDurationOptions(): Array<{ text: string; value: number; disabled?: boolean }> {
      return [
        {
          text: "Remove current break (0 months)",
          value: 0,
          disabled: !(this as any).isSelectedTalentOnABreak
        },
        { text: "3 months", value: 3 },
        { text: "6 months", value: 6 }
      ];
    },
    manualBreakConfirmationCopy(): string {
      const overlay = (this as any).selectedMembershipOverlay;
      const until = overlay?.onABreakUntil
        ? this.formatDateTime(overlay.onABreakUntil)
        : null;
      if ((this as any).isSelectedTalentOnABreak) {
        return until
          ? `This Talent is currently On a Break until ${until}. Choose 3 or 6 months to update it, or 0 months to remove the break.`
          : "This Talent is currently On a Break. Choose 3 or 6 months to update it, or 0 months to remove the break.";
      }
      return "This puts the Talent on a temporary FFS break immediately. A support note is required and will be saved to the Talent profile.";
    },
    canApplyMembershipAction(): boolean {
      if (!(this as any).selectedMembershipAction || (this as any).isApplyingMembershipAction) {
        return false;
      }
      if ((this as any).isManualNotFitAction) {
        return !!(
          ((this as any).manualNotFitForm.note || "").trim() &&
          ((this as any).manualNotFitForm.ticketReference || "").trim()
        );
      }
      if ((this as any).isManualBreakAction) {
        const duration = Number((this as any).manualBreakForm.durationMonths);
        const validDuration = [0, 3, 6].includes(duration);
        const note = ((this as any).manualBreakForm.note || "").trim();
        const evidenceReference = ((this as any).manualBreakForm.evidenceReference || "").trim();
        return !!(
          validDuration &&
          note &&
          evidenceReference &&
          (duration !== 0 || (this as any).isSelectedTalentOnABreak)
        );
      }
      return true;
    },
    filteredHygienists(): Hygienist[] {
      const hygienists = [...(this as any).hygienists];

      // For "All" tab: return data as-is (server-side pagination/search handles everything)
      if ((this as any).currentTab === 2) {
        return hygienists;
      }

      // For other tabs (New Members Review, First Shift Booked), apply client-side filtering
      let filtered = hygienists;

      const query = (this as any).searchQuery?.trim() || "";

      if (query.length > 0) {
        const strippedQuery = query.replace(/[\s\-()+ ]/g, "");
        const queryAsNumber = parseInt(strippedQuery);
        const isPurelyNumeric = !isNaN(queryAsNumber) && strippedQuery === queryAsNumber.toString();

        if (isPurelyNumeric && strippedQuery.length >= 7) {
          // Phone number search (7+ digits) — also match by ID
          filtered = filtered.filter(
            (h: Hygienist) =>
              h.id === queryAsNumber ||
              (h.phone && h.phone.toString().replace(/[\s\-()+ ]/g, "").includes(strippedQuery))
          );
        } else if (isPurelyNumeric) {
          // Short number — ID search only
          filtered = filtered.filter((h: Hygienist) => h.id === queryAsNumber);
        } else if (/^[A-Za-z]{2}$/.test(query)) {
          // T2-6512: 2-letter province/state code — province only (see fetchHygienists)
          const q = query.toLowerCase();
          filtered = filtered.filter(
            (h: Hygienist) => h.province && h.province.toLowerCase().includes(q)
          );
        } else if (query.length >= 3) {
          // Text search - search across all relevant fields (T2-6512 adds city/province)
          const searchLower = query.toLowerCase();
          filtered = filtered.filter(
            (h: Hygienist) =>
              (h.firstName &&
                h.firstName.toLowerCase().includes(searchLower)) ||
              (h.lastName && h.lastName.toLowerCase().includes(searchLower)) ||
              (h.email && h.email.toLowerCase().includes(searchLower)) ||
              (h.city && h.city.toLowerCase().includes(searchLower)) ||
              (h.province && h.province.toLowerCase().includes(searchLower)) ||
              (h.phone && h.phone.toString().includes(query)) ||
              (h.CDHONumber && h.CDHONumber.toString().includes(query))
          );
        }
      }

      return filtered;
    },
    hasSearchCriteria(): boolean {
      // Check if search query has valid input
      const query = (this as any).searchQuery?.trim() || "";
      if (query.length === 0) return false;

      // ID search (any length), 2-letter province/state code, or text search (min 3 chars)
      const queryAsNumber = parseInt(query);
      const isNumericId =
        !isNaN(queryAsNumber) && query === queryAsNumber.toString();

      return isNumericId || query.length >= 3 || /^[A-Za-z]{2}$/.test(query);
    },
    // T2-6361: the AP Profile Information chip is FFS-owned and normally reads
    // the raw FFS currentStatusCode (Active/Pro/Elite/RL1/CourseRequired/...).
    // T2-6366: a live manual Take a Break overlay is a support-applied terminal
    // hold, so it must override the raw FFS code in the visible AP chip.
    profileStatusCode(): string | null {
      const overlayStatusCode = this.getMembershipOverlayStatusCode(
        (this as any).selectedMembership
      );
      if (overlayStatusCode) {
        return overlayStatusCode;
      }
      const code =
        (this as any).profileInfo?.currentStatusCode ??
        (this as any).selectedHygienist?.currentStatusCode;
      return code ? (code as string) : null;
    },
    profileStatusLabel(): string {
      // Per Holly's decision the chip shows the raw FFS code (e.g. "RL1"), except
      // for active support overlays that are themselves the visible membership
      // state, such as "On a Break".
      const code = (this as any).profileStatusCode;
      if (!code) {
        // No MembershipManagement row — graceful fallback + an engineering
        // follow-up warning (ticket AC scenario 3 / QA Recording #916).
        const id =
          (this as any).profileInfo?.id ??
          (this as any).selectedHygienist?.id ??
          "?";
        // eslint-disable-next-line no-console
        console.warn(
          `[T2-6361] Talent ${id} has no MembershipManagement.currentStatusCode; rendering "Unknown" status chip.`
        );
        return "Unknown";
      }
      return this.getStatusLabel(code);
    },
    profileStatusColor(): string {
      // T2-6516: resolve recovery overlays through the shared FFS helper so the
      // profile chip colour is the exact same hex as the Talent table row.
      const overlayCode = recoveryOverlayToDisplayCode(
        (this as any).selectedMembershipOverlay?.state
      );
      return this.getStatusColor(overlayCode || (this as any).profileStatusCode);
    },
    profileStatusChipDark(): boolean {
      // T2-6516: mirror the table's `:dark="!statusNeedsDarkText(code)"` so text
      // contrast matches (e.g. amber Course Required reads with dark text).
      const overlayCode = recoveryOverlayToDisplayCode(
        (this as any).selectedMembershipOverlay?.state
      );
      const code = overlayCode || (this as any).profileStatusCode;
      return !this.statusNeedsDarkText(code);
    },
    profileValueGives(): number | null {
      return (
        (this as any).profileInfo?.valueGives ??
        (this as any).selectedHygienist?.valueGives ??
        null
      );
    },
    profileRoleLabel(): string {
      const type =
        (this as any).profileInfo?.type ??
        (this as any).selectedHygienist?.type;
      return this.getRoleLabel(typeof type === "number" ? type : null);
    },
    profileBlockedUntilFormatted(): string {
      const blocked =
        (this as any).profileInfo?.blockedUntil ??
        (this as any).selectedHygienist?.blockedUntil;
      return this.formatDateTime(blocked);
    },
    profileAddress(): string {
      const info = (this as any).profileInfo;
      const current = (this as any).selectedHygienist;
      const addressParts = [
        info?.address || current?.address,
        info?.city || current?.city,
        info?.province || current?.province
      ].filter(Boolean);
      const countryId = info?.country ?? current?.country ?? null;
      const countryName = this.formatCountryName(countryId);
      if (countryName) {
        addressParts.push(countryName);
      }
      return addressParts.length > 0 ? addressParts.join(", ") : "N/A";
    },
    profileQualifications(): string[] {
      return (this as any).profileInfo?.qualifications || [];
    },
    // T2-6513: DA Level editor is Assistants only (Hygienist.type === 1).
    isDentalAssistantProfile(): boolean {
      const type =
        (this as any).profileInfo?.type ??
        (this as any).selectedHygienist?.type ??
        null;
      return Number(type) === 1;
    },
    profileSkills(): string[] {
      return (this as any).profileInfo?.skills || [];
    },
    // T2-6227: real work-experience rows + resume links from the profile fetch.
    profileWorkExperience(): WorkExperienceRow[] {
      const we = (this as any).profileInfo?.workExperience;
      return Array.isArray(we) ? we : [];
    },
    profileResumeUrl(): string | null {
      return (this as any).profileInfo?.resumeUrl || null;
    },
    profileHireResumeUrl(): string | null {
      return (this as any).profileInfo?.hireResumeUrl || null;
    },
    profileSkillsText(): string {
      const skills = this.profileSkills;
      return skills.length > 0 ? skills.join("\n") : "";
    },
    profileAboutText(): string {
      const info =
        (this as any).profileInfo?.about ||
        (this as any).selectedHygienist?.about;
      return info && info.trim().length > 0
        ? info
        : "No profile information provided.";
    },
    // T2-6546: Admin/Reception shift preference helpers.
    isCanadianDentalAssistant(): boolean {
      const type =
        (this as any).profileInfo?.type ??
        (this as any).selectedHygienist?.type ??
        null;
      const country =
        (this as any).profileInfo?.country ??
        (this as any).selectedHygienist?.country ??
        null;
      return Number(type) === 1 && Number(country) === 0;
    },
    adminPreferenceValue(): string | null {
      return (
        (this as any).profileInfo?.agreedAdmin ??
        (this as any).selectedHygienist?.agreedAdmin ??
        null
      );
    },
    adminPreferenceLabel(): string {
      const value = (this as any).adminPreferenceValue;
      if (!value) return "No decision";
      const year = new Date(value).getFullYear();
      if (year <= 1970) return "Opted out (permanently declined)";
      return `Opted in — ${(this as any).formatDate(value)}`;
    },
    adminPreferenceChipColor(): string {
      const value = (this as any).adminPreferenceValue;
      if (!value) return "grey";
      const year = new Date(value).getFullYear();
      if (year <= 1970) return "#FB7672"; // opted out (coral)
      return "#13CA82"; // opted in (green)
    },
    adminPreferenceConfirmText(): string {
      const name = (this as any).selectedHygienist
        ? `${(this as any).selectedHygienist.firstName} ${(this as any).selectedHygienist.lastName}`
        : "this Talent";
      switch ((this as any).adminPreferenceDialog.preference) {
        case "optIn":
          return `Set ${name} as OPTED IN to Admin/Reception shifts? Admin/Reception shifts (Job.type 2) will be able to appear for this member.`;
        case "optOut":
          return `Set ${name} as OPTED OUT (permanently declined)? Admin/Reception shifts will stay hidden for this member.`;
        case "reset":
          return `Reset ${name}'s Admin/Reception preference to NO DECISION? The member will be eligible to see the in-app opt-in prompt again (Canadian DA + good-standing gates still apply).`;
        default:
          return "";
      }
    },
    profilePhone(): string {
      const phone =
        (this as any).profileInfo?.phone ||
        (this as any).selectedHygienist?.phone;
      return phone && phone.toString().trim().length > 0 ? phone : "N/A";
    },
    profilePhoto(): string {
      const photo =
        (this as any).profileInfo?.photoUrl ||
        (this as any).selectedHygienist?.photoUrl;
      return photo && photo.length > 0 ? photo : DEFAULT_PROFILE_PHOTO;
    },
    profileSignupCompletion(): number | string | boolean | null | undefined {
      const self = this as any;
      return self.profileInfo
        ? self.profileInfo.isComplete
        : self.selectedHygienist?.isComplete;
    },
    computedTableHeaders(): TableHeader[] {
      const currentTabValue: number = (this as any).currentTab;

      // For New Members Review tab (currentTab === 0), use custom column order with status after lastName
      if (currentTabValue === 0) {
        // Custom order: id, firstName, lastName, status, valueGives, then rest of columns
        const memberReviewColumnsReordered: string[] = [
          "id",
          "firstName",
          "lastName",
          "status",
          "isComplete",
          "valueGives",
          "type",
          "level",
          "address",
          "city",
          "province",
          "country",
          "postalCode",
          "CDHONumber",
          "phone",
          "email",
          "graduationYear",
          "resumeUrl",
          "createdOn",
          "isDupe"
        ];
        const data: TableHeader[] = memberReviewColumnsReordered.map(
          (i: string): TableHeader => {
            return {
              align: "left",
              text: i === "isComplete" ? "Sign-up" : startCase(i),
              value: i,
              sortable: true
            };
          }
        );
        return [
          ...data,
          {
            align: "left",
            text: "User shifts",
            value: "userShifts",
            sortable: false
          },
          { align: "left", text: "Actions", value: "actions", sortable: false }
        ];
      }

      // For First Shift Booked tab (currentTab === 1), use firstShiftBooked columns
      if (currentTabValue === 1) {
        const tableColumns: string[] = [
          ...constants.enabledColumns.firstShiftBooked
        ];
        tableColumns.splice(tableColumns.indexOf("status") + 1, 0, "isComplete");
        const data: TableHeader[] = tableColumns.map(
          (i: string): TableHeader => {
            return {
              align: "left",
              text: i === "isComplete" ? "Sign-up" : startCase(i),
              value: i,
              sortable: true
            };
          }
        );
        return [
          ...data,
          {
            align: "left",
            text: "User shifts",
            value: "userShifts",
            sortable: false
          },
          { align: "left", text: "Actions", value: "actions", sortable: false }
        ];
      }

      // For "All" tab, use managed headers if available
      const headers =
        (this as any).managedTalentHeaders.length > 0
          ? (this as any).managedTalentHeaders
          : (this as any).hygienistHeaders;

      return headers as TableHeader[];
    },
    hygienistNotes(): string | null {
      // Get Notes from selectedHygienist if available
      return (this as any).selectedHygienist?.Notes || null;
    },
    cancellationSummary(): { 
      total: number; 
      counted: number; 
      ignored: number;
      grouped: number;
      gracePeriod: number; 
      beforeCleanSlate: number; 
      cleanSlateTimestamp: string | null 
    } {
      const cancellations = (this as any).cancellations || [];
      const cleanSlateTimestamp = cancellations.length > 0 ? cancellations[0]?.cleanSlateTimestamp : null;
      
      let counted = 0;
      let ignored = 0;
      let grouped = 0;
      let gracePeriod = 0;
      let beforeCleanSlate = 0;
      
      for (const c of cancellations) {
        if (c.adminMode === "ignored") {
          ignored++;
        } else if (c.adminMode === "grouped") {
          grouped++;
        } else if (c.countsTowardReliability || c.countsTowardBlacklist) {
          counted++;
        } else if (c.isGracePeriodCancellation) {
          gracePeriod++;
        } else {
          beforeCleanSlate++;
        }
      }
      
      return {
        total: cancellations.length,
        counted,
        ignored,
        grouped,
        gracePeriod,
        beforeCleanSlate,
        cleanSlateTimestamp
      };
    },
    cancellationModeOptions(): Array<{ text: string; value: CancellationAdminMode }> {
      return [
        { text: "Scored", value: "scored" },
        { text: "Ignored", value: "ignored" },
        { text: "Grouped", value: "grouped" }
      ];
    },
    hasCancellationReliabilityChanges(): boolean {
      const cancellations = ((this as any).cancellations || []) as Cancellation[];
      return cancellations.some((c: Cancellation) => {
        const groupedWith = c.adminMode === "grouped" ? c.adminGroupedWith || null : null;
        return (
          c.adminMode !== c.originalAdminMode ||
          groupedWith !== (c.originalGroupedWith || null) ||
          this.hasManualCancellationScoreChange(c) ||
          this.hasAutoCancellationScoreChange(c) ||
          (c.reason || "") !== (c.originalReason || "") ||
          (c.details || "") !== (c.originalDetails || "")
        );
      });
    },
    canSubmitCancellationCorrection(): boolean {
      return !!(
        (this as any).hasCancellationReliabilityChanges &&
        ((this as any).cancellationCorrectionReason || "").trim() &&
        ((this as any).cancellationEvidenceReference || "").trim() &&
        (this as any).cancellationCorrectionIdempotencyKey
      );
    },
    officeCancellationLeavingOnBehalf(): boolean {
      const dialog = (this as any).officeCancellationEditDialog;
      if (!dialog || !dialog.open) return false;
      return (
        dialog.originalReason === "onBehalfOfTalent" &&
        dialog.reason !== "onBehalfOfTalent"
      );
    },
    canSaveOfficeCancellationEdit(): boolean {
      const dialog = (this as any).officeCancellationEditDialog;
      if (!dialog || !dialog.open || dialog.saving || !dialog.item) return false;
      const dirty =
        String(dialog.reason || "") !== String(dialog.item.reason || "") ||
        String(dialog.details || "") !== String(dialog.item.details || "");
      const acknowledgementOk =
        dialog.reason !== "onBehalfOfTalent" ||
        dialog.onBehalfAcknowledged === true;
      return dirty && acknowledgementOk;
    },
    canSubmitRatingCorrection(): boolean {
      const dialog = (this as any).ratingCorrectionDialog;
      const value = Number(dialog?.value);
      return !!(
        dialog?.open &&
        !dialog?.submitting &&
        Number.isInteger(value) &&
        value >= 1 &&
        value <= 5 &&
        String(dialog?.reason || "").trim() &&
        String(dialog?.evidenceReference || "").trim() &&
        dialog?.idempotencyKey
      );
    }
  },
  created() {
    // Set the table name in the store so data loads correctly
    this.$store.commit("modify", { property: "tablename", value: "Hygienist" });
    // Initialize countDays in store if not already set
    if (!this.$store.state.countDays) {
      this.$store.commit("modify", { property: "countDays", value: 3 });
    } else {
      (this as any).countDays = this.$store.state.countDays;
    }
    // T2-6225: when navigated to via an EntityIdLink (?hygienistId=N), seed the
    // search so the board filters straight to that talent.
    const linkedHygienistId = (this as any).$route?.query?.hygienistId;
    if (linkedHygienistId) {
      (this as any).searchQuery = String(linkedHygienistId);
    }
    // tableOptions is already initialized in data() - no need to set again
    this.fetchHygienists();
    (this as any).loadCourseResetPermission();
  },
  beforeDestroy() {
    // Clean up debounce timer
    if ((this as any).searchDebounceTimer) {
      clearTimeout((this as any).searchDebounceTimer);
    }
  },
  watch: {
    "$store.state.tableContent": {
      handler(newData) {
        if (
          this.$store.state.tablename === "Hygienist" &&
          newData &&
          Array.isArray(newData) &&
          newData.length > 0 &&
          newData[0].firstName !== undefined
        ) {
          (this as any).hygienists = newData;
          (this as any).loadingHygienists = false;
        }
      },
      immediate: true
    },
    // Watch for tab changes and refetch data
    currentTab: {
      handler(newValue: number, oldValue: number | undefined) {
        // Reset pagination and search when changing tabs
        (this as any).totalHygienists = 0;
        (this as any).searchQuery = "";
        (this as any).tableOptions = {
          page: 1,
          itemsPerPage: 15
        } as DataTableOptions;
        (this as any).fetchHygienists();
      }
    },
    // Watch for pagination changes (only for "All" tab with server-side pagination)
    tableOptions: {
      handler(
        newOptions: DataTableOptions,
        oldOptions: DataTableOptions | undefined
      ) {
        // Don't fetch if currently fetching from search
        if ((this as any).isFetchingFromSearch) {
          return;
        }

        // T2-6362: also refetch when the sort column/direction changes so the
        // server can re-order by FFS tier rank (status column).
        const sortChanged =
          JSON.stringify(newOptions.sortBy || []) !==
            JSON.stringify((oldOptions && oldOptions.sortBy) || []) ||
          JSON.stringify(newOptions.sortDesc || []) !==
            JSON.stringify((oldOptions && oldOptions.sortDesc) || []);

        // Only fetch if options changed and we're on the "All" tab
        // Don't fetch on initial load
        if (
          (this as any).currentTab === 2 &&
          oldOptions &&
          oldOptions.page !== undefined &&
          (newOptions.page !== oldOptions.page ||
            newOptions.itemsPerPage !== oldOptions.itemsPerPage ||
            sortChanged)
        ) {
          (this as any).fetchHygienists();
        }
      },
      deep: true
    },
    // Watch for search query changes
    searchQuery: {
      handler() {
        (this as any).handleSearchChange();
      }
    },
    showMembershipActionsModal(open: boolean) {
      if (
        open &&
        ((this as any).selectedMembershipAction || "")
          .replace(/\s+/g, "")
          .toLowerCase() === "cleanslate"
      ) {
        (this as any).selectedMembershipAction = "";
      }
      if (!open) {
        (this as any).resetManualBreakForm();
        (this as any).resetManualNotFitForm();
      }
    },
    selectedMembershipAction(action: string) {
      (this as any).membershipActionIdempotencyKey = [
        "Not a Fit",
        "Undo Manual Not a Fit",
        "Take a Break"
      ].includes(action)
        ? createAdminSourceCorrectionIdempotencyKey(`manual-membership-${action}`)
        : "";
      if (action !== "Take a Break") {
        (this as any).resetManualBreakForm();
      } else if ((this as any).manualBreakForm.durationMonths === null) {
        (this as any).manualBreakForm.durationMonths = 3;
      }
      if (!["Not a Fit", "Undo Manual Not a Fit"].includes(action)) {
        (this as any).resetManualNotFitForm();
      }
    }
  },
  methods: {
    credentialFieldLabel,
    credentialDisplay,
    async openEducationEditor(talent: Hygienist) {
      const requestId = ++(this as any).educationRequestId;
      try {
        const fresh = await getHygienistProfile(talent.id);
        if (requestId !== (this as any).educationRequestId) return;
        (this as any).educationTalent = { ...talent, ...fresh };
        (this as any).educationDialog = true;
      } catch (error) {
        (this as any).showSnackbar("Could not load Education & Credentials. Refresh the profile and try again.", "error");
      }
    },
    educationRefreshed(talent: EducationTalent) {
      (this as any).patchHygienistListRow(talent.id, talent);
      if (Number((this as any).selectedHygienist?.id) === Number(talent.id)) {
        (this as any).selectedHygienist = { ...(this as any).selectedHygienist, ...talent };
        if ((this as any).profileInfo) (this as any).profileInfo = { ...(this as any).profileInfo, ...talent };
      }
    },
    async educationSaved(result: { id: number; credentials: EducationTalent; recomputeStatus: string }) {
      (this as any).educationRefreshed({ ...result.credentials, id: result.id });
      const pending = !["completed", "not_required"].includes(result.recomputeStatus);
      (this as any).showSnackbar(pending
        ? "Education & Credentials saved. Membership recalculation is pending and will retry."
        : "Education & Credentials saved.", pending ? "warning" : "success");
      const refreshed = await (this as any).fetchHygienists();
      if (Number((this as any).selectedHygienist?.id) === Number(result.id)) {
        await (this as any).fetchHygienistDetails(result.id);
      }
      if (refreshed === false) (this as any).showSnackbar("Saved. Refresh the Talent list to reload all details.", "warning");
    },
    shouldShowCancellationsOverviewSaveHelper,
    // T2-6449: Open the admin-only FFS Support Quick Reference PDF in a new tab.
    // The asset is gated behind an admin endpoint, so we fetch it with the admin
    // token and open the resulting object URL. The tab is opened synchronously
    // first to preserve the user gesture (popup-blocker safe) and its opener is
    // severed for _blank/noopener-equivalent protection.
    async openFfsReferenceSheet() {
      // Open a placeholder tab synchronously so the later navigation keeps the
      // user activation (popup-blocker safe). Once the auth-gated request
      // resolves we point that tab at the fetched blob.
      const newTab = window.open("about:blank", "_blank");
      (this as any).ffsReferenceLoading = true;
      let objectUrl: string | null = null;
      try {
        const blob = await fetchFfsReferenceSheet();
        objectUrl = URL.createObjectURL(blob);
        if (newTab) {
          // Sever the opener for _blank/noopener-equivalent protection.
          try {
            newTab.opener = null;
          } catch (e) {
            /* opener already inaccessible — safe to ignore */
          }
          newTab.location.href = objectUrl;
        } else {
          // Popup blocked — fall back to a safe anchor-triggered new tab.
          const link = document.createElement("a");
          link.href = objectUrl;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        // Revoke after the new tab has had time to load the blob.
        const urlToRevoke = objectUrl;
        window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60000);
      } catch (err) {
        if (newTab) {
          newTab.close();
        }
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        (this as any).showSnackbar(
          "Could not open the FFS Reference Sheet. Please try again.",
          "error"
        );
      } finally {
        (this as any).ffsReferenceLoading = false;
      }
    },

    resetManualBreakForm() {
      (this as any).manualBreakForm = {
        durationMonths: 3,
        note: "",
        evidenceReference: ""
      };
    },

    resetManualNotFitForm() {
      (this as any).manualNotFitForm = {
        note: "",
        ticketReference: ""
      };
    },

    closeMembershipActionsModal() {
      (this as any).showMembershipActionsModal = false;
      (this as any).selectedMembershipAction = "";
      (this as any).resetManualBreakForm();
      (this as any).resetManualNotFitForm();
      (this as any).membershipActionIdempotencyKey = "";
    },

    // T2-6229: About Me manual override.
    startAboutEdit() {
      const current =
        (this as any).profileInfo?.about ||
        (this as any).selectedHygienist?.about ||
        "";
      (this as any).aboutDraft = current;
      (this as any).aboutError = "";
      (this as any).editingAbout = true;
    },
    cancelAboutEdit() {
      (this as any).editingAbout = false;
      (this as any).aboutDraft = "";
      (this as any).aboutError = "";
    },
    // Client-side mirror of the server's contact-info rules (server is the
    // source of truth — this just gives immediate feedback).
    detectAboutContactInfo(text: string): string | null {
      if (/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(text)) {
        return "email address";
      }
      if (/(\+?\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/.test(text)) {
        return "phone number";
      }
      if (
        /(https?:\/\/|www\.)\S+/i.test(text) ||
        /\b[a-z0-9-]+\.(com|ca|net|org|io|co|us|info|biz)\b/i.test(text)
      ) {
        return "URL / website";
      }
      return null;
    },
    async saveAbout() {
      const self = this as any;
      if (!self.selectedHygienist || !self.selectedHygienist.id) return;
      const draft = String(self.aboutDraft || "").trim();
      const violation = this.detectAboutContactInfo(draft);
      if (violation) {
        self.aboutError = `About Me cannot contain contact information (detected: ${violation}).`;
        return;
      }
      self.savingAbout = true;
      self.aboutError = "";
      try {
        await updateHygienistAbout(self.selectedHygienist.id, draft);
        if (self.profileInfo) {
          self.profileInfo.about = draft;
        }
        self.selectedHygienist.about = draft;
        self.editingAbout = false;
        this.$store.commit("callSnackbar", {
          visible: true,
          text: "About Me updated (admin edit logged in Notes).",
          color: "#13CA82"
        });
      } catch (error) {
        self.aboutError =
          error instanceof Error ? error.message : "Failed to save About Me";
      } finally {
        self.savingAbout = false;
      }
    },
    // T2-6546: open the confirm dialog for an Admin/Reception preference change.
    confirmAdminPreference(preference: "optIn" | "optOut" | "reset") {
      const self = this as any;
      if (!self.isCanadianDentalAssistant) return;
      self.adminPreferenceDialog = { open: true, preference };
    },
    // T2-6546: apply the confirmed Admin/Reception preference change.
    async applyAdminPreference() {
      const self = this as any;
      const preference = self.adminPreferenceDialog.preference as
        | "optIn"
        | "optOut"
        | "reset";
      if (!preference || !self.selectedHygienist || !self.selectedHygienist.id) {
        return;
      }
      self.savingAdminPreference = preference;
      try {
        const result = await updateHygienistAdminPreference(
          self.selectedHygienist.id,
          preference
        );
        const newValue = result?.agreedAdmin ?? null;
        if (self.profileInfo) {
          self.profileInfo.agreedAdmin = newValue;
        }
        self.selectedHygienist.agreedAdmin = newValue;
        self.adminPreferenceDialog = { open: false, preference: "" };
        this.$store.commit("callSnackbar", {
          visible: true,
          text: "Admin/Reception preference updated (logged in Notes).",
          color: "#13CA82"
        });
      } catch (error) {
        this.$store.commit("callSnackbar", {
          visible: true,
          text:
            error instanceof Error
              ? error.message
              : "Failed to update Admin/Reception preference.",
          color: "#FB7672"
        });
      } finally {
        self.savingAdminPreference = "";
      }
    },
    // T2-6225: modifier / middle clicks fall through to the native anchor
    // (new tab); a plain click is prevented so the row click selects instead.
    onIdChipClick(event: MouseEvent) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      ) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
    },
    async fetchHygienists(throwOnError = false): Promise<boolean> {
      (this as any).loadingHygienists = true;

      try {
        let hygienistRecords: Hygienist[] = [];
        const currentTabValue: number = (this as any).currentTab;

        if (currentTabValue === 0) {
          // New Members Review - use filterDays with 'memberreview'
          const memberReviewRecords = await filterDays(
            (this as any).countDays,
            "memberreview"
          );
          hygienistRecords = Array.isArray(memberReviewRecords)
            ? (memberReviewRecords as Hygienist[])
            : [];
          (this as any).totalHygienists = hygienistRecords.length;
        } else if (currentTabValue === 1) {
          // First Shift Booked - use filterDays with 'firstshiftbooked'
          const firstShiftRecords = await filterDays(
            (this as any).countDays,
            "firstshiftbooked"
          );
          // Format dates like FirstShiftBooked.vue does
          const formattedRecords =
            Array.isArray(firstShiftRecords) && firstShiftRecords.length
              ? formatDates(firstShiftRecords)
              : [];
          // This endpoint returns no plain `id`, only `HygienistId`/`JobId`, while
          // handleRowClick and every membership action read `hygienist.id`. See the
          // helper for the outage that caused.
          hygienistRecords = normalizeFirstShiftBookedRows(
            formattedRecords as any[]
          ) as Hygienist[];
          (this as any).totalHygienists = hygienistRecords.length;
        } else {
          // All - use pure server-side pagination with search
          const columnNames: string[] = [
            "id",
            "firstName",
            "lastName",
            "email",
            "address",
            "city",
            "province",
            "postalCode",
            "country",
            "phone",
            "CDHONumber",
            "school",
            "graduationYear",
            "type",
            "status",
            "isComplete",
            "accountStatus",
            "createdOn",
            "completedJobs",
            "about",
            "blockedUntil",
            "etransferEmail",
            "photoUrl",
            "Notes",
            "InstabookActivated",
            "userId",
            "stripeAccountId",
            "stripeCreatedTimestamp",
            "Verified"
          ];

          // Build search with all searchable columns
          let searchString: string = "";
          let searchColumns: string[] = [];

          const query = (this as any).searchQuery?.trim() || "";

          if (query.length > 0) {
            const strippedQuery = query.replace(/[\s\-()+ ]/g, "");
            const queryAsNumber = parseInt(strippedQuery);
            const isPurelyNumeric = !isNaN(queryAsNumber) && strippedQuery === queryAsNumber.toString();

            if (isPurelyNumeric && strippedQuery.length >= 7) {
              // Phone number search (7+ digits) — also include ID
              searchString = strippedQuery;
              searchColumns = ["id", "phone"];
            } else if (isPurelyNumeric) {
              // Short number — ID search only
              searchString = strippedQuery;
              searchColumns = ["id"];
            } else if (/^[A-Za-z]{2}$/.test(query)) {
              // T2-6512: 2-letter province/state code (e.g. ON, NY, BC). These are
              // below the 3-char text gate, so match them against province only
              // (searching city here would false-match cities containing the code).
              searchString = query;
              searchColumns = ["province"];
            } else if (query.length >= 3) {
              // Text search - search across all relevant fields (T2-6512 adds city/province)
              searchString = query;
              searchColumns = [
                "firstName",
                "lastName",
                "email",
                "phone",
                "CDHONumber",
                "city",
                "province"
              ];
            }
          }

          const options: DataTableOptions = (this as any).tableOptions;
          const page: number = options.page || 1;
          const itemsPerPage: number = options.itemsPerPage || 15;
          // Status and sign-up sort before pagination; other columns retain
          // the historical server ordering.
          const sortByCol: string | undefined =
            Array.isArray(options.sortBy) && options.sortBy.length
              ? options.sortBy[0]
              : undefined;
          const sortDescVal: boolean =
            Array.isArray(options.sortDesc) && options.sortDesc.length
              ? Boolean(options.sortDesc[0])
              : true;

          // Build request params
          const requestParams: paginateTable = {
            tableName: "Hygienist",
            limit: itemsPerPage,
            page: page,
            columnNames
          };

          if (sortByCol === "status" || sortByCol === "isComplete") {
            // Cast guards against a stale local dev-server TS program; the
            // paginateTable interface declares these fields for clean builds.
            (requestParams as any).sortBy = sortByCol;
            (requestParams as any).sortDesc = sortDescVal;
          }

          if (searchString.length > 0 && searchColumns.length > 0) {
            requestParams.search = searchString;
            requestParams.searchColumns = searchColumns;
          }

          // Get total count (refresh when searching changes)
          if ((this as any).totalHygienists === 0 || searchString.length > 0) {
            const totalCountParams: {
              tableName: string;
              columnNames: string[];
              search?: string;
              searchColumns?: string[];
            } = {
              tableName: "Hygienist",
              columnNames
            };

            if (searchString.length > 0 && searchColumns.length > 0) {
              totalCountParams.search = searchString;
              totalCountParams.searchColumns = searchColumns;
            }

            const totalCount: number = await totalItemsByTable(
              totalCountParams
            );
            (this as any).totalHygienists = Number(totalCount) || 0;
          }

          // Fetch page data with search applied
          const paginatedRecords = await getPaginateItems(requestParams);
          hygienistRecords = Array.isArray(paginatedRecords)
            ? (paginatedRecords as Hygienist[])
            : [];
        }

        const tableHeaders: any[] =
          hygienistRecords.length > 0 ? getHeaders(hygienistRecords) : [];

        (this as any).$store.commit("modifyMultiple", [
          {
            property: "tableHeaders",
            value: tableHeaders
          },
          {
            property: "tableContent",
            value: hygienistRecords
          }
        ]);

        (this as any).hygienists = hygienistRecords;
        return true;
      } catch (error) {
        if (throwOnError) {
          throw error;
        }
        const tabLabel: string = (this as any).describeCurrentTab(
          (this as any).currentTab
        );
        const message: string =
          error instanceof Error ? error.message : "Unknown error occurred";
        (this as any).hygienists = [];
        (this as any).totalHygienists = 0;
        (this as any).showSnackbar(
          `Error fetching ${tabLabel}: ${message}`,
          "error"
        );
        return false;
      } finally {
        (this as any).loadingHygienists = false;
      }
    },

    /**
     * Returns a human-readable label for the selected tab.
     */
    describeCurrentTab(tabIndex: number): string {
      const tabLabels: string[] = [
        "New Members Review",
        "First Shift Booked",
        "All Talent"
      ];
      return tabLabels[tabIndex] ?? "Talent";
    },

    /**
     * Refresh data with the current countDays value (for tabs 0 and 1)
     */
    refreshWithDays() {
      // Update the store with the new countDays value
      this.$store.commit("modify", {
        property: "countDays",
        value: (this as any).countDays
      });
      // Refresh the data
      (this as any).fetchHygienists();
    },

    handleSearchChange() {
      // Clear existing timer
      if ((this as any).searchDebounceTimer) {
        clearTimeout((this as any).searchDebounceTimer);
      }

      // For "All" tab, debounce search and reset to page 1
      if ((this as any).currentTab === 2) {
        (this as any).searchDebounceTimer = setTimeout(() => {
          // Set flag to prevent tableOptions watcher from double-fetching
          (this as any).isFetchingFromSearch = true;

          // Reset total count to force refresh with search
          (this as any).totalHygienists = 0;

          // Reset to page 1
          const currentOptions: DataTableOptions = (this as any).tableOptions;
          (this as any).tableOptions = {
            page: 1,
            itemsPerPage: currentOptions.itemsPerPage || 15
          } as DataTableOptions;

          // Fetch with search applied
          (this as any).fetchHygienists().finally(() => {
            (this as any).isFetchingFromSearch = false;
          });
        }, 500);
      }
      // For other tabs, computed property handles filtering
    },

    handleRowClick(hygienist: Hygienist) {
      // Clear previous data immediately when selecting a new hygienist
      (this as any).jobs = [];
      (this as any).invoices = [];
      (this as any).payments = [];
      (this as any).notes = [];
      (this as any).duplicates = [];
      clearCourseSelectionForTalentSwitch(this as any);
      (this as any).profileInfo = null;
      (this as any).officeIdToBlock = null;
      (this as any).selectedMembershipAction = "";
      (this as any).resetManualBreakForm();
      (this as any).resetManualNotFitForm();
      (this as any).resetStripeRequestState();
      invalidateStripeStatusRequest(this as any);
      (this as any).stripeAccountStatus = null;
      (this as any).stripeStatusError = "";
      (this as any).stripeRequestLink = "";

      // T2-6514 (QA fix): the Blocks & Favorites section must default to collapsed
      // whenever a different Talent profile is opened. Without this reset the section
      // kept its previous expanded state when navigating between profiles.
      (this as any).collapsedSections.blocksFavourites = true;

      // Set the selected hygienist
      (this as any).selectedHygienist = hygienist;
      // T2-6228: seed the (now inline) editable School field on selection.
      (this as any).editableSchool = hygienist.school || "";
      // T2-6513: reset DA Level editor when switching Talent profiles.
      (this as any).cancelDaLevelEdit();
      (this as any).$store.commit("modify", {
        property: "selectedHygienistId",
        value: hygienist.id
      });

      // Fetch fresh data for this hygienist
      (this as any).fetchHygienistDetails(hygienist.id);
      (this as any).loadStripeAccountStatus(hygienist.id);
      if (!(this as any).collapsedSections.courses) {
        (this as any).fetchCourses(hygienist.id);
      }
    },

    async loadStripeAccountStatus(hygienistId: number) {
      const requestedId = Number(hygienistId);
      (this as any).stripeStatusError = "";
      (this as any).stripeRequestLink = "";
      await loadCurrentStripeStatus({
        context: this as any,
        entityId: requestedId,
        selectedEntityId: () => Number((this as any).selectedHygienist?.id),
        fetchStatus: () => getAdminStripeAccountStatus(requestedId),
        applyStatus: status => {
          (this as any).stripeAccountStatus = status;
        },
        onError: error => {
          (this as any).stripeAccountStatus = null;
          (this as any).stripeStatusError =
            error instanceof Error ? error.message : "Unable to load Stripe status";
        }
      });
    },

    resetStripeRequestState() {
      invalidateStripeRequest(this as any);
      (this as any).stripeRequestLink = "";
    },

    async requestStripeInformationLink() {
      const hygienistId = Number((this as any).selectedHygienist?.id);
      if (!hygienistId) return;
      const requestId = beginStripeRequest(this as any);
      (this as any).stripeRequestLink = "";
      try {
        const result = await createAdminStripeRequestInformationLink(hygienistId);
        if (
          !isCurrentStripeRequest(this as any, requestId) ||
          Number((this as any).selectedHygienist?.id) !== hygienistId
        ) return;
        (this as any).stripeRequestLink = result.url;
        (this as any).showSnackbar("Fresh Stripe Request Information link generated", "success");
      } catch (error) {
        if (
          !isCurrentStripeRequest(this as any, requestId) ||
          Number((this as any).selectedHygienist?.id) !== hygienistId
        ) return;
        (this as any).showSnackbar(
          error instanceof Error ? error.message : "Unable to generate Stripe link",
          "error"
        );
      } finally {
        finishStripeRequest(this as any, requestId);
      }
    },

    async fetchHygienistDetails(
      hygienistId: number,
      options: {
        shouldCommit?: () => boolean;
        throwOnError?: boolean;
        refreshOpenPanels?: boolean;
      } = {}
    ): Promise<boolean> {
      try {
        const tolerateDetailError = (
          request: Promise<any>,
          fallback: any
        ): Promise<any> =>
          options.throwOnError ? request : request.catch(() => fallback);
        // Use new dedicated API endpoints for hygienist-specific data
        const [
          jobsData,
          invoicesData,
          notesData,
          duplicatesData,
          userData,
          profileData,
          feedbackCount
        ] = await Promise.all([
          tolerateDetailError(getHygienistJobs(hygienistId), []),
          tolerateDetailError(getHygienistInvoices(hygienistId), []),
          tolerateDetailError(getHygienistNotesDetailed(hygienistId), []),
          tolerateDetailError(getPotentialDuplicates(hygienistId), []),
          tolerateDetailError(getUserByHygienist(hygienistId), []),
          tolerateDetailError(getHygienistProfile(hygienistId), null),
          tolerateDetailError(getHygienistFeedbackCount(hygienistId), 0)
        ]);

        if (options.shouldCommit && !options.shouldCommit()) {
          return false;
        }

        // Assign filtered data (already filtered by backend)
        (this as any).jobs = Array.isArray(jobsData) ? jobsData : [];
        (this as any).invoices = Array.isArray(invoicesData) ? invoicesData : [];
        (this as any).notes = Array.isArray(notesData) ? notesData : [];
        (this as any).duplicates = Array.isArray(duplicatesData) ? duplicatesData : [];
        if (options.refreshOpenPanels !== false) {
          // Reset feedback when switching hygienists
          (this as any).feedback = [];
          (this as any).feedbackTotal = typeof feedbackCount === 'number' ? feedbackCount : 0;
          (this as any).feedbackOptions = { page: 1, itemsPerPage: 5, sortBy: ['shiftDate'], sortDesc: [true] };
          if ((this as any).feedbackAbortController) {
            (this as any).feedbackAbortController.abort();
            (this as any).feedbackAbortController = null;
          }
          // If the feedback panel is already open, reload data for the new hygienist
          if (!(this as any).collapsedSections.feedback) {
            (this as any).fetchFeedback(hygienistId);
          }
          // T2-6478: same for the Job Rating panel if it is already open.
          (this as any).jobRatings = [];
          if (!(this as any).collapsedSections.jobRatings) {
            (this as any).fetchJobRatings(hygienistId);
          }
        }

        // Payments - empty for now (will implement Stripe /v1/charges/search separately)
        (this as any).payments = [];

        const hygienistIdNumber = Number(hygienistId);
        const normalizeHygienistId = (value: unknown): number | null => {
          if (value === null || typeof value === "undefined") {
            return null;
          }
          const numericValue = Number(value);
          return Number.isNaN(numericValue) ? null : numericValue;
        };

        let userEmail: string | null = null;
        if (Array.isArray(userData)) {
          const typedUsers = userData as UserRecord[];
          const matchingUser = typedUsers.find(user => {
            const userHygienistId = normalizeHygienistId(user.hygienistId);
            return (
              userHygienistId !== null && userHygienistId === hygienistIdNumber
            );
          });

          if (matchingUser) {
            userEmail = matchingUser.email || null;
          } else if (typedUsers.length === 1) {
            userEmail = typedUsers[0].email || null;
          }
        } else if (userData && typeof userData === "object") {
          const singleUser = userData as UserRecord;
          const singleUserHygienistId = normalizeHygienistId(
            singleUser.hygienistId
          );
          if (
            singleUserHygienistId === null ||
            singleUserHygienistId === hygienistIdNumber
          ) {
            userEmail = singleUser.email || null;
          }
        }

        const resolvedEmail =
          (profileData && profileData.email) ||
          userEmail ||
          ((this as any).selectedHygienist?.email ?? null);

        if (profileData) {
          const refreshedNotes =
            typeof profileData.Notes === "string"
              ? profileData.Notes
              : (this as any).selectedHygienist.Notes || null;
          const refreshedMembership = profileData.membership || null;
          const enhancedProfile: HygienistProfile = {
            ...profileData,
            email: resolvedEmail
          };

          (this as any).profileInfo = enhancedProfile;
          (this as any).selectedHygienist = {
            ...(this as any).selectedHygienist,
            graduationYear: profileData.graduationYear,
            firstLicensedYear: profileData.firstLicensedYear,
            firstLicensedMonth: profileData.firstLicensedMonth,
            educationCredentialsConfirmedOn: profileData.educationCredentialsConfirmedOn,
            phone:
              profileData.phone ||
              (this as any).selectedHygienist.phone ||
              "N/A",
            about:
              profileData.about || (this as any).selectedHygienist.about || "",
            blockedUntil: profileData.blockedUntil,
            photoUrl:
              profileData.photoUrl || (this as any).selectedHygienist.photoUrl,
            Notes: refreshedNotes,
            email: resolvedEmail || "N/A",
            membership: refreshedMembership,
            currentStatusCode:
              profileData.currentStatusCode ??
              (this as any).selectedHygienist.currentStatusCode ??
              null
          };
          (this as any).patchHygienistListRow(hygienistIdNumber, {
            Notes: refreshedNotes,
            membership: refreshedMembership,
            currentStatusCode: enhancedProfile.currentStatusCode
          });
        } else {
          (this as any).profileInfo = null;
          (this as any).selectedHygienist = {
            ...(this as any).selectedHygienist,
            email: resolvedEmail || "N/A",
            membership: null
          };
        }
        return true;
      } catch (error) {
        if (options.throwOnError) {
          throw error;
        }
        (this as any).showSnackbar("Error fetching hygienist details", "error");
        return false;
      }
    },

    patchHygienistListRow(hygienistId: number, patch: Partial<Hygienist>) {
      const rows = ((this as any).hygienists || []) as Hygienist[];
      const rowIndex = rows.findIndex(
        (h: Hygienist) => Number(h.id) === Number(hygienistId)
      );
      if (rowIndex === -1) {
        return;
      }

      const updatedRows = [...rows];
      updatedRows.splice(rowIndex, 1, {
        ...updatedRows[rowIndex],
        ...patch
      });

      (this as any).hygienists = updatedRows;
      (this as any).$store.commit("modifyMultiple", [
        {
          property: "tableContent",
          value: updatedRows
        }
      ]);
    },

    syncSelectedHygienistFromList(hygienistId: number) {
      const updatedHygienist = ((this as any).hygienists || []).find(
        (h: Hygienist) => Number(h.id) === Number(hygienistId)
      );
      if (!updatedHygienist || !(this as any).selectedHygienist) {
        return;
      }

      (this as any).selectedHygienist = {
        ...(this as any).selectedHygienist,
        ...updatedHygienist,
        membership:
          (this as any).profileInfo?.membership ||
          updatedHygienist.membership ||
          (this as any).selectedHygienist.membership ||
          null
      };
    },

    async initGooglePlaces() {
      if ((this as any).autocompleteService) return;
      try {
        await new Promise<void>((resolve, reject) => {
          if ((window as any).google?.maps?.places) {
            resolve();
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyB1eG57t-eolKSOUPgtZZowNa0_qEO8whs&libraries=places';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Google Maps'));
          document.head.appendChild(script);
        });
        const google = (window as any).google;
        (this as any).autocompleteService = new google.maps.places.AutocompleteService();
        const dummyDiv = document.createElement('div');
        (this as any).placesService = new google.maps.places.PlacesService(dummyDiv);
        (this as any).autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
      } catch (err) {
        console.error('Failed to init Google Places:', err);
      }
    },

    startAddressEdit() {
      const info = (this as any).profileInfo;
      const current = (this as any).selectedHygienist;
      (this as any).editedAddress = {
        address: info?.address || current?.address || '',
        city: info?.city || current?.city || '',
        province: info?.province || current?.province || '',
        postalCode: info?.postalCode || current?.postalCode || '',
        country: info?.country ?? current?.country ?? 0,
        lat: info?.lat || current?.lat || '',
        lon: info?.lon || current?.lon || '',
      };
      (this as any).addressSearchText = '';
      (this as any).addressPredictions = [];
      (this as any).editingAddress = true;
      this.initGooglePlaces();
    },

    cancelAddressEdit() {
      (this as any).editingAddress = false;
      (this as any).addressSearchText = '';
      (this as any).addressPredictions = [];
    },

    onAddressInput() {
      const text = (this as any).addressSearchText;
      if (!text || text.length < 3 || !(this as any).autocompleteService) {
        (this as any).addressPredictions = [];
        return;
      }
      const info = (this as any).profileInfo;
      const current = (this as any).selectedHygienist;
      const countryId = info?.country ?? current?.country ?? 0;
      const countryCode = countryId === 1 ? 'us' : 'ca';

      (this as any).autocompleteService.getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: countryCode },
          types: ['address'],
          sessionToken: (this as any).autocompleteSessionToken,
        },
        (predictions: any[], status: string) => {
          if (status !== 'OK' || !predictions) {
            (this as any).addressPredictions = [];
            return;
          }
          const BLOCKED_STATES = ['California', 'Alaska', 'CA', 'AK'];
          (this as any).addressPredictions = predictions.filter((p: any) => {
            return !BLOCKED_STATES.some((s) => p.description.includes(s));
          });
        }
      );
    },

    selectAddressPrediction(prediction: any) {
      if (!(this as any).placesService) return;
      (this as any).placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'geometry', 'formatted_address'],
          sessionToken: (this as any).autocompleteSessionToken,
        },
        (place: any, status: string) => {
          if (status !== 'OK' || !place) return;
          const parsed = this.parseAddressComponents(place.address_components);
          (this as any).editedAddress = {
            address: parsed.address,
            city: parsed.city,
            province: parsed.province,
            postalCode: parsed.postalCode,
            country: (this as any).editedAddress.country,
            lat: place.geometry?.location?.lat()?.toString() || '',
            lon: place.geometry?.location?.lng()?.toString() || '',
          };
          (this as any).addressSearchText = place.formatted_address || prediction.description;
          (this as any).addressPredictions = [];
          const google = (window as any).google;
          (this as any).autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
        }
      );
    },

    parseAddressComponents(components: any[]): { address: string; city: string; province: string; postalCode: string } {
      let streetNumber = '';
      let route = '';
      let city = '';
      let province = '';
      let postalCode = '';
      let subpremise = '';

      for (const comp of components) {
        const types = comp.types || [];
        if (types.includes('street_number')) streetNumber = comp.long_name;
        else if (types.includes('route')) route = comp.long_name;
        else if (types.includes('locality')) city = comp.long_name;
        else if (types.includes('sublocality_level_1') && !city) city = comp.long_name;
        else if (types.includes('administrative_area_level_1')) province = comp.short_name;
        else if (types.includes('postal_code')) postalCode = comp.long_name;
        else if (types.includes('subpremise')) subpremise = comp.long_name;
      }

      let address = streetNumber ? `${streetNumber} ${route}` : route;
      if (subpremise) address = `${subpremise}-${address}`;
      return { address, city, province, postalCode };
    },

    async saveAddress() {
      const hyg = (this as any).selectedHygienist;
      if (!hyg || !hyg.id) return;

      (this as any).addressSaving = true;
      try {
        await updateHygienist(hyg.id, {
          address: (this as any).editedAddress.address,
          city: (this as any).editedAddress.city,
          province: (this as any).editedAddress.province,
          postalCode: (this as any).editedAddress.postalCode,
          country: (this as any).editedAddress.country,
          lat: (this as any).editedAddress.lat,
          lon: (this as any).editedAddress.lon,
        });

        if ((this as any).profileInfo) {
          (this as any).profileInfo.address = (this as any).editedAddress.address;
          (this as any).profileInfo.city = (this as any).editedAddress.city;
          (this as any).profileInfo.province = (this as any).editedAddress.province;
          (this as any).profileInfo.postalCode = (this as any).editedAddress.postalCode;
          (this as any).profileInfo.lat = (this as any).editedAddress.lat;
          (this as any).profileInfo.lon = (this as any).editedAddress.lon;
        }

        (this as any).editingAddress = false;
        (this as any).addressPredictions = [];
        (this as any).showSnackbar("Address updated successfully", "success");
      } catch (err) {
        console.error('Failed to save address:', err);
        (this as any).showSnackbar("Failed to update address", "error");
      } finally {
        (this as any).addressSaving = false;
      }
    },

    hydrateCancellationAdminState(rows: Cancellation[]): Cancellation[] {
      return rows.map((row: Cancellation) => {
        let adminMode: CancellationAdminMode = "scored";
        if (row.isIgnored) {
          adminMode = "ignored";
        } else if (row.groupedWith) {
          adminMode = "grouped";
        }

        const groupedWith = row.groupedWith ? Number(row.groupedWith) : null;
        const cancellationScore = this.normalizeCancellationScore(row.cancellationScore);

        return {
          ...row,
          adminMode,
          originalAdminMode: adminMode,
          adminGroupedWith: adminMode === "grouped" ? groupedWith : null,
          originalGroupedWith: adminMode === "grouped" ? groupedWith : null,
          adminCancellationScore: adminMode === "scored" ? cancellationScore : null,
          originalCancellationScore: adminMode === "scored" ? cancellationScore : null,
          adminCancellationScoreEdited: false,
          originalReason: row.reason || "",
          originalDetails: row.details || "",
          reason: row.reason || "",
          details: row.details || ""
        };
      });
    },

    async handleCancellationsOverview() {
      if (!(this as any).selectedHygienist) return;

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        // T2-6367: endpoint now returns { cancellations, weightedReliability }.
        const { cancellations, weightedReliability } =
          await getHygienistReliabilityCancellations(hygienistId);
        const hydrated = this.hydrateCancellationAdminState(
          Array.isArray(cancellations) ? cancellations : []
        );

        (this as any).cancellations = hydrated;
        (this as any).ignoredCancellations = hydrated.filter(
          (c: Cancellation) => c.adminMode === "ignored"
        );
        (this as any).cancellationWeightedReliability = weightedReliability;
        (this as any).cancellationReliabilityNote = "";
        (this as any).cancellationCorrectionReason = "";
        (this as any).cancellationEvidenceReference = "";
        (this as any).cancellationCorrectionIdempotencyKey =
          createAdminSourceCorrectionIdempotencyKey(
            `cancellation-${hygienistId}`
          );
        (this as any).showCancellationsModal = true;
      } catch (error) {
        (this as any).showSnackbar("Error fetching cancellations", "error");
      }
    },

    officeCancellationReasonLabel(reason: string | null | undefined): string {
      const code = String(reason || "");
      const match = OFFICE_CANCELLATION_REASON_OPTIONS.find(
        option => option.value === code
      );
      return match ? match.text : code || "—";
    },

    async handleOfficeCancellations() {
      if (!(this as any).selectedHygienist) return;
      (this as any).loadingOfficeCancellations = true;
      try {
        const hygienistId = (this as any).selectedHygienist.id;
        const { cancellations } = await getTalentOfficeCancellations(hygienistId);
        (this as any).officeCancellations = Array.isArray(cancellations)
          ? cancellations
          : [];
        (this as any).showOfficeCancellationsModal = true;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Error fetching office cancellations";
        (this as any).showSnackbar(message, "error");
      } finally {
        (this as any).loadingOfficeCancellations = false;
      }
    },

    openOfficeCancellationEdit(item: TalentOfficeCancellation) {
      const storedReason = String(item.reason || "");
      const isCanonicalReason = OFFICE_CANCELLATION_REASON_OPTIONS.some(
        option => option.value === storedReason
      );
      (this as any).officeCancellationReasonOptions = isCanonicalReason
        ? [...OFFICE_CANCELLATION_REASON_OPTIONS]
        : [
            {
              text: storedReason || "(blank / legacy)",
              value: storedReason
            },
            ...OFFICE_CANCELLATION_REASON_OPTIONS
          ];
      (this as any).officeCancellationEditDialog = {
        open: true,
        item,
        reason: storedReason,
        details: item.details || "",
        originalReason: storedReason,
        onBehalfAcknowledged: false,
        supportNote: "",
        noteError: "",
        saving: false
      };
    },

    handleOfficeCancellationReasonChange(reason: string) {
      if (reason !== "onBehalfOfTalent") {
        (this as any).officeCancellationEditDialog.onBehalfAcknowledged = false;
      }
    },

    closeOfficeCancellationEdit() {
      (this as any).officeCancellationReasonOptions = [
        ...OFFICE_CANCELLATION_REASON_OPTIONS
      ];
      (this as any).officeCancellationEditDialog = {
        open: false,
        item: null,
        reason: "",
        details: "",
        originalReason: "",
        onBehalfAcknowledged: false,
        supportNote: "",
        noteError: "",
        saving: false
      };
    },

    async saveOfficeCancellationEdit() {
      const dialog = (this as any).officeCancellationEditDialog;
      const hygienist = (this as any).selectedHygienist;
      if (!dialog || !dialog.item || !hygienist) return;
      if (
        dialog.reason === "onBehalfOfTalent" &&
        dialog.onBehalfAcknowledged !== true
      ) {
        return;
      }

      const supportNote = String(dialog.supportNote || "").trim();
      if (!supportNote) {
        dialog.noteError = "Support note is required";
        return;
      }
      dialog.noteError = "";
      dialog.saving = true;
      try {
        const reasonChanged =
          String(dialog.reason || "") !== String(dialog.item.reason || "");
        const payload: {
          reason?: string;
          details: string;
          supportNote: string;
        } = {
          details: dialog.details || "",
          supportNote
        };
        if (reasonChanged) {
          payload.reason = dialog.reason;
        }
        await updateTalentOfficeCancellation(
          hygienist.id,
          dialog.item.id,
          payload
        );
        (this as any).showSnackbar("Office cancellation updated", "success");
        this.closeOfficeCancellationEdit();
        const { cancellations } = await getTalentOfficeCancellations(
          hygienist.id
        );
        (this as any).officeCancellations = Array.isArray(cancellations)
          ? cancellations
          : [];
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Error saving office cancellation";
        (this as any).showSnackbar(message, "error");
      } finally {
        dialog.saving = false;
      }
    },

    getMainCancellationOptions(item: Cancellation): Array<{ text: string; value: number }> {
      const cancellations = ((this as any).cancellations || []) as Cancellation[];
      return cancellations
        .filter((c: Cancellation) => c.id !== item.id && c.adminMode === "scored")
        .map((c: Cancellation) => ({
          value: c.id,
          text: `#${c.id} / Job ${c.jobId} - ${this.formatDate(c.shiftDate || "")}`
        }));
    },

    handleCancellationModeChange(item: Cancellation) {
      if (item.adminMode === "grouped") {
        const options = this.getMainCancellationOptions(item);
        const hasSelectedMain = options.some(
          (option: { value: number }) => option.value === item.adminGroupedWith
        );
        item.adminGroupedWith = hasSelectedMain ? item.adminGroupedWith || null : options[0]?.value || null;
      } else {
        item.adminGroupedWith = null;
      }

      if (item.adminMode === "ignored" || item.adminMode === "grouped") {
        item.adminCancellationScore = null;
        item.adminCancellationScoreEdited = false;
      } else if (item.originalAdminMode === "scored") {
        item.adminCancellationScore = item.originalCancellationScore || 0;
        item.adminCancellationScoreEdited = false;
      } else {
        item.adminCancellationScore = null;
        item.adminCancellationScoreEdited = false;
      }

      const scoredIds = new Set(
        (((this as any).cancellations || []) as Cancellation[])
          .filter((c: Cancellation) => c.adminMode === "scored")
          .map((c: Cancellation) => c.id)
      );

      (((this as any).cancellations || []) as Cancellation[]).forEach((c: Cancellation) => {
        if (c.adminMode !== "grouped") {
          return;
        }
        if (c.adminGroupedWith && scoredIds.has(c.adminGroupedWith)) {
          return;
        }
        const replacement = this.getMainCancellationOptions(c)[0];
        c.adminGroupedWith = replacement?.value || null;
      });
    },

    normalizeCancellationScore(score: number | string | null | undefined): number {
      const numericScore = Number(score || 0);
      if (!Number.isFinite(numericScore) || numericScore < 0) {
        return 0;
      }
      return Number(numericScore.toFixed(2));
    },

    markCancellationScoreEdited(item: Cancellation) {
      item.adminCancellationScoreEdited = true;
    },

    isCancellationScoreBlank(score: number | string | null | undefined): boolean {
      return score === null || score === undefined || score === "";
    },

    getCancellationReasonOptions(item: Cancellation) {
      const current = String(item.reason || "").trim();
      if (current && !TALENT_CANCELLATION_REASON_CODES.has(current)) {
        return [
          { value: current, text: `${current} (historical)` },
          ...TALENT_CANCELLATION_REASON_OPTIONS
        ];
      }
      return TALENT_CANCELLATION_REASON_OPTIONS;
    },

    hasManualCancellationScoreChange(item: Cancellation): boolean {
      if (item.adminMode !== "scored") {
        return false;
      }
      if (this.isCancellationScoreBlank(item.adminCancellationScore)) {
        return false;
      }
      const currentScore = this.normalizeCancellationScore(item.adminCancellationScore);
      const originalScore = this.normalizeCancellationScore(item.originalCancellationScore);
      return Boolean(item.adminCancellationScoreEdited) || currentScore !== originalScore;
    },

    hasAutoCancellationScoreChange(item: Cancellation): boolean {
      return (
        item.adminMode === "scored" &&
        item.originalAdminMode === "scored" &&
        this.isCancellationScoreBlank(item.adminCancellationScore) &&
        !this.isCancellationScoreBlank(item.originalCancellationScore)
      );
    },

    async readTalentMembershipSnapshot(
      hygienistId: number
    ): Promise<MembershipSnapshot | null> {
      try {
        const [profile, stats] = await Promise.all([
          getHygienistProfile(hygienistId).catch(() => null),
          getHygienistStats(hygienistId).catch(() => null)
        ]);
        if (!profile && !stats) {
          return null;
        }
        return buildMembershipSnapshot(profile, stats);
      } catch {
        return null;
      }
    },

    beginTalentCorrectionReconciliation(): number {
      const requestId = Number(
        ((this as any).membershipReconciliationRequestId || 0) + 1
      );
      (this as any).membershipReconciliationRequestId = requestId;
      return requestId;
    },

    async reconcileTalentCorrection(
      hygienistId: number,
      baseline: MembershipSnapshot | null,
      response: any,
      requestId: number
    ): Promise<MembershipReconciliationOutcome> {
      return reconcileCorrectionMembership({
        baseline,
        response,
        shouldContinue: () =>
          !(this as any)._isDestroyed &&
          !(this as any)._isBeingDestroyed &&
          Number((this as any).selectedHygienist?.id) === hygienistId &&
          Number((this as any).membershipReconciliationRequestId) === requestId,
        readSnapshot: async () => {
          const current = await (this as any).readTalentMembershipSnapshot(
            hygienistId
          );
          if (!current) {
            throw new Error("Unable to refresh membership state");
          }
          return current;
        }
      });
    },

    async refreshTalentCorrectionViews(
      hygienistId: number,
      requestId: number
    ): Promise<"refreshed" | "cancelled" | "failed"> {
      const shouldContinue = () =>
        !(this as any)._isDestroyed &&
        !(this as any)._isBeingDestroyed &&
        Number((this as any).selectedHygienist?.id) === hygienistId &&
        Number((this as any).membershipReconciliationRequestId) === requestId;
      try {
        const refreshOutcome = await refreshCorrectionViews({
          refreshList: () => (this as any).fetchHygienists(true),
          shouldContinue,
          refreshDetails: () =>
            (this as any).fetchHygienistDetails(hygienistId, {
              shouldCommit: shouldContinue,
              throwOnError: true,
              refreshOpenPanels: false
            }),
          isStatsOpen: () => Boolean((this as any).showStatsDialog),
          refreshStats: async () => {
            const stats = await getHygienistStats(hygienistId);
            if (!shouldContinue()) {
              return false;
            }
            (this as any).hygienistStats = stats;
            return true;
          }
        });
        return refreshOutcome.cancelled ? "cancelled" : "refreshed";
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Error refreshing Talent";
        (this as any).showSnackbar(
          `Correction saved, but the Dashboard could not refresh all Talent data: ${message}`,
          "warning"
        );
        return "failed";
      }
    },

    isCancellationIncludedInReliability(item: Cancellation): boolean {
      if (item.adminMode !== "scored" || item.isGracePeriodCancellation) {
        return false;
      }
      return Boolean(
        item.countsTowardReliability ||
        item.countsTowardBlacklist ||
        this.normalizeCancellationScore(item.adminCancellationScore || item.cancellationScore) > 0
      );
    },

    formatCancellationScore(score: number | string | null | undefined): string {
      const numericScore = Number(score || 0);
      if (Number.isNaN(numericScore)) {
        return "0";
      }
      return Number.isInteger(numericScore) ? String(numericScore) : numericScore.toFixed(2);
    },

    async saveCancellationReliabilityChanges() {
      if (!(this as any).selectedHygienist) {
        (this as any).showSnackbar("Please select a hygienist first", "warning");
        return;
      }

      const cancellations = ((this as any).cancellations || []) as Cancellation[];
      const invalidManualScore = cancellations.find((c: Cancellation) => {
        if (
          c.adminMode !== "scored" ||
          this.isCancellationScoreBlank(c.adminCancellationScore)
        ) {
          return false;
        }
        const score = Number(c.adminCancellationScore);
        return !Number.isFinite(score) || score < 0 || score > 9999.99;
      });
      if (invalidManualScore) {
        (this as any).showSnackbar("Cancellation scores must be between 0 and 9999.99", "warning");
        return;
      }

      const adjustments: CancellationReliabilityAdjustment[] = cancellations.map((c: Cancellation) => {
        const adjustment: CancellationReliabilityAdjustment = {
          id: c.id,
          mode: c.adminMode || "scored",
          groupedWith: c.adminMode === "grouped" ? c.adminGroupedWith || null : null
        };

        if (c.adminMode === "scored") {
          if (this.isCancellationScoreBlank(c.adminCancellationScore)) {
            adjustment.scoreMode = "auto";
          } else if (this.hasManualCancellationScoreChange(c)) {
            adjustment.scoreMode = "manual";
            adjustment.cancellationScore = this.normalizeCancellationScore(c.adminCancellationScore);
          } else {
            adjustment.scoreMode = "preserve";
          }
        }

        const currentReason = String(c.reason || "");
        const originalReason = String(c.originalReason || "");
        if (currentReason !== originalReason) {
          adjustment.reason = currentReason;
        }
        const currentDetails = String(c.details || "");
        const originalDetails = String(c.originalDetails || "");
        if (currentDetails !== originalDetails) {
          adjustment.details = currentDetails;
        }

        return adjustment;
      });

      const invalidGrouped = adjustments.find(
        (adjustment: CancellationReliabilityAdjustment) =>
          adjustment.mode === "grouped" && !adjustment.groupedWith
      );
      if (invalidGrouped) {
        (this as any).showSnackbar("Choose a main cancellation for each grouped row", "warning");
        return;
      }

      const scoredIds = new Set(
        adjustments
          .filter((adjustment: CancellationReliabilityAdjustment) => adjustment.mode === "scored")
          .map((adjustment: CancellationReliabilityAdjustment) => adjustment.id)
      );
      const groupedWithNonScored = adjustments.find(
        (adjustment: CancellationReliabilityAdjustment) =>
          adjustment.mode === "grouped" && !scoredIds.has(Number(adjustment.groupedWith))
      );
      if (groupedWithNonScored) {
        (this as any).showSnackbar("Grouped cancellations must point to a scored cancellation", "warning");
        return;
      }

      (this as any).savingCancellationReliability = true;
      try {
        const hygienistId = Number((this as any).selectedHygienist.id);
        const reconciliationRequestId = (this as any)
          .beginTalentCorrectionReconciliation();
        const baseline = await (this as any).readTalentMembershipSnapshot(
          hygienistId
        );
        const result = await saveHygienistReliabilityCancellations(
          hygienistId,
          adjustments,
          ((this as any).cancellationReliabilityNote || "").trim(),
          {
            reason: ((this as any).cancellationCorrectionReason || "").trim(),
            evidenceReference: ((this as any).cancellationEvidenceReference || "").trim()
          },
          (this as any).cancellationCorrectionIdempotencyKey
        );
        const hydrated = this.hydrateCancellationAdminState(
          Array.isArray(result?.cancellations) ? result.cancellations : []
        );

        (this as any).cancellations = hydrated;
        (this as any).ignoredCancellations = hydrated.filter(
          (c: Cancellation) => c.adminMode === "ignored"
        );
        // T2-6367: refresh the server-sourced weighted reliability after recalc.
        if (result && result.weightedReliability != null) {
          (this as any).cancellationWeightedReliability = Number(
            result.weightedReliability
          );
        } else {
          try {
            const refreshed = await getHygienistReliabilityCancellations(
              hygienistId
            );
            (this as any).cancellationWeightedReliability =
              refreshed.weightedReliability;
          } catch (e) {
            // Non-fatal: keep the pre-save value.
          }
        }
        (this as any).cancellationReliabilityNote = "";
        (this as any).cancellationCorrectionReason = "";
        (this as any).cancellationEvidenceReference = "";
        (this as any).cancellationCorrectionIdempotencyKey =
          createAdminSourceCorrectionIdempotencyKey(
            `cancellation-${hygienistId}`
          );
        if (result?.saveResult && result.saveResult.reliabilityTouched === false) {
          (this as any).showSnackbar(
            "Cancellation reason/details saved successfully",
            "success"
          );
          return;
        }
        const outcome = await (this as any).reconcileTalentCorrection(
          hygienistId,
          baseline,
          result,
          reconciliationRequestId
        );
        if (outcome.status === "cancelled") {
          return;
        }
        const refreshStatus = await (this as any).refreshTalentCorrectionViews(
          hygienistId,
          reconciliationRequestId
        );
        if (refreshStatus !== "refreshed") {
          return;
        }
        const message = correctionResultMessage("Cancellations", outcome);
        (this as any).showSnackbar(message.text, message.color);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Error saving cancellations";
        (this as any).showSnackbar(message, "error");
      } finally {
        (this as any).savingCancellationReliability = false;
      }
    },

    async confirmMembershipAction() {
      if (
        !(this as any).selectedHygienist ||
        !(this as any).selectedMembershipAction
      ) {
        (this as any).showSnackbar("Please select an action", "warning");
        return;
      }

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        const action = (this as any).selectedMembershipAction;
        const hygienist = (this as any).selectedHygienist;

        if (action.replace(/\s+/g, "").toLowerCase() === "cleanslate") {
          (this as any).selectedMembershipAction = "";
          (this as any).showSnackbar(
            "This membership action is no longer available.",
            "warning"
          );
          return;
        }

        if ((this as any).isManualNotFitAction) {
          if (!(this as any).canApplyMembershipAction) {
            (this as any).showSnackbar(
              "Ticket reference and support note are required.",
              "warning"
            );
            return;
          }

          (this as any).isApplyingMembershipAction = true;
          const payload = {
            ticketReference: ((this as any).manualNotFitForm.ticketReference || "").trim(),
            note: ((this as any).manualNotFitForm.note || "").trim()
          };

          const successMessage = await runManualNotAFitAction({
            action,
            hygienistId,
            idempotencyKey: (this as any).membershipActionIdempotencyKey,
            payload,
            apply: applyManualNotAFit,
            undo: undoManualNotAFit
          });

          (this as any).showSnackbar(successMessage, "success");
          (this as any).closeMembershipActionsModal();
          await (this as any).fetchHygienists();
          if (hygienistId) {
            await (this as any).fetchHygienistDetails(hygienistId);
          }
          return;
        }

        if ((this as any).isManualBreakAction) {
          if (!(this as any).canApplyMembershipAction) {
            (this as any).showSnackbar(
              "Break duration, support note, and evidence reference are required.",
              "warning"
            );
            return;
          }

          const durationMonths = Number((this as any).manualBreakForm.durationMonths);
          (this as any).isApplyingMembershipAction = true;
          await runManualBreakAction({
            hygienistId,
            durationMonths,
            note: (this as any).manualBreakForm.note || "",
            evidenceReference:
              (this as any).manualBreakForm.evidenceReference || "",
            idempotencyKey: (this as any).membershipActionIdempotencyKey,
            apply: applyManualBreak
          });

          (this as any).showSnackbar(
            durationMonths === 0
              ? "Take a Break removed"
              : `Take a Break updated for ${durationMonths} months`,
            "success"
          );
          (this as any).closeMembershipActionsModal();
          await (this as any).fetchHygienists();
          (this as any).syncSelectedHygienistFromList(hygienistId);
          if (hygienistId) {
            await (this as any).fetchHygienistDetails(hygienistId);
          }
          return;
        }

        (this as any).isApplyingMembershipAction = true;

        // Get email if not available - check multiple sources
        let hygienistEmail = hygienist.email;
        
        // First check hygienistStats if available
        if (!hygienistEmail && (this as any).hygienistStats?.email) {
          hygienistEmail = (this as any).hygienistStats.email;
        }
        
        // If still not found, fetch from API
        if (!hygienistEmail) {
          try {
            const userData = await getUserByHygienist(hygienistId);
            if (Array.isArray(userData) && userData.length > 0) {
              hygienistEmail = userData[0].email || "";
            } else if (userData && typeof userData === "object" && (userData as any).email) {
              hygienistEmail = (userData as any).email;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error fetching user email:", error);
          }
        }

        // For actions that require email, validate it exists.
        // T2-6364: removed "Cancellations Only" / "Negative Feedback" /
        // "Quiz Received" (legacy course-dispute flows). "Take a Break"
        // (T2-6366) is handled above via the manual-break dialog and needs no email.
        const actionsRequiringEmail = ["First Response"];
        if (actionsRequiringEmail.includes(action) && !hygienistEmail) {
          (this as any).showSnackbar(
            "Hygienist email not found. Cannot perform this action.",
            "error"
          );
          return;
        }

        switch (action) {
          case "First Response":
            await updateCourseDisputeAnalyzer({
              hygId: hygienistId,
              userName: hygienist.firstName || "",
              userEmail: hygienistEmail || "",
              formType: 0,
              templateMessage: 0, // Default value, can be enhanced with modal later
              notesToMember: "",
              internalNotes: "",
              ticketNumber: ""
            });
            break;

          // T2-6364/T2-6381: "Cancellations Only", "Negative Feedback" and
          // "Quiz Received" cases were removed along with their legacy
          // cancellationCode/reinstatementCode course-code derivation.

          default:
            throw new Error(`Unknown action: ${action}`);
        }

        (this as any).showSnackbar(
          "Membership action applied successfully",
          "success"
        );
        (this as any).closeMembershipActionsModal();
        
        // Refresh the list and selected hygienist details
        await (this as any).fetchHygienists();
        if (hygienistId) {
          await (this as any).fetchHygienistDetails(hygienistId);
        }
      } catch (error) {
        const message = actionableAdminActionError(
          error,
          "Unknown error occurred"
        );
        (this as any).showSnackbar(
          `Error applying membership action: ${message}`,
          "error"
        );
        // eslint-disable-next-line no-console
        console.error("Error applying membership action:", error);
      } finally {
        (this as any).isApplyingMembershipAction = false;
      }
    },

    async addNoteToHygienist(hygienistId: number, noteText: string) {
      try {
        const timestamp = new Date().toLocaleString();
        const note = `${timestamp}: ${noteText}`;

        // Add note to Hygienist.Notes column (not Notes table)
        await updateHygienistNotes({
          id: hygienistId,
          note: note
        });

        // Refresh hygienist data to update Notes column in table and detail view
        await (this as any).fetchHygienists();

        // Refresh selected hygienist details if currently selected
        if (
          (this as any).selectedHygienist &&
          (this as any).selectedHygienist.id === hygienistId
        ) {
          // Update selectedHygienist from refreshed list to get updated Notes
          const updatedHygienist = (this as any).hygienists.find(
            (h: Hygienist) => h.id === hygienistId
          );
          if (updatedHygienist) {
            (this as any).selectedHygienist = updatedHygienist;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error adding note to hygienist:", error);
        // Don't throw - note addition failure shouldn't block the main action
      }
    },

    async confirmApprove() {
      if (!(this as any).canReviewSelectedSignup) return;
      await (this.$refs.signupReviewDialog as any).open((this as any).selectedHygienist);
    },
    markSelectedSignupReviewed(hygienistId: number) {
      if (Number((this as any).selectedHygienist?.id) === hygienistId) {
        (this as any).selectedHygienist = { ...(this as any).selectedHygienist, reviewed: 1 };
      }
    },
    async onSignupApproved(hygienistId: number) {
      (this as any).markSelectedSignupReviewed(hygienistId);
      await (this as any).fetchHygienists();
      (this as any).showSnackbar("Talent approved successfully", "success");
      if (Number((this as any).selectedHygienist?.id) === hygienistId) {
        (this as any).fetchHygienistDetails(hygienistId);
      }
    },

    async confirmHold() {
      if (!(this as any).selectedHygienist) return;

      // For New Member Review tab, open modal to collect message
      if ((this as any).currentTab === 0) {
        const hygienist = (this as any).selectedHygienist;
        await (this as any).openMemberReviewModal(
          hygienist,
          "hold",
          "Hold Account - Not a Fit"
        );
        return;
      }

      // For other tabs, use direct update (existing behavior)
      try {
        const hygienistId = (this as any).selectedHygienist.id;
        await fetchWithAuth(`v3/admin/hygienist/${hygienistId}`, {
          method: "PUT",
          body: JSON.stringify({ status: -1, reviewed: 1 }) // -1 = Probation/Hold, reviewed = 1
        });

        (this as any).showSnackbar(
          "Hygienist put on hold successfully",
          "success"
        );
        await (this as any).fetchHygienists(); // Refresh the list
        // Update selectedHygienist from refreshed list and refresh details
        if (hygienistId) {
          const updatedHygienist = (this as any).hygienists.find(
            (h: Hygienist) => h.id === hygienistId
          );
          if (updatedHygienist) {
            (this as any).selectedHygienist = updatedHygienist;
          }
          (this as any).fetchHygienistDetails(hygienistId);
        }
      } catch (error) {
        (this as any).showSnackbar("Error putting hygienist on hold", "error");
      }
    },

    async confirmBlock() {
      if (!(this as any).selectedHygienist) return;

      const isNewMemberReview = (this as any).currentTab === 0;

      // T2-6429: on the New Members Review tab, Block routes to the manual
      // Not a Fit flow (required note + ticket reference) instead of writing a
      // legacy Revoked status. Other tabs keep their existing direct behavior.
      if (isNewMemberReview) {
        await (this as any).openMemberReviewModal(
          (this as any).selectedHygienist,
          "block",
          "Block Account - Not a Fit"
        );
        return;
      }

      const status = -2;

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        await fetchWithAuth(`v3/admin/hygienist/${hygienistId}`, {
          method: "PUT",
          body: JSON.stringify({ status: status, reviewed: 1 })
        });

        (this as any).showSnackbar("Hygienist blocked successfully", "success");
        await (this as any).fetchHygienists();
        if (hygienistId) {
          const updatedHygienist = (this as any).hygienists.find(
            (h: Hygienist) => h.id === hygienistId
          );
          if (updatedHygienist) {
            (this as any).selectedHygienist = updatedHygienist;
          }
          (this as any).fetchHygienistDetails(hygienistId);
        }
      } catch (error) {
        (this as any).showSnackbar("Error blocking hygienist", "error");
      }
    },

    // First Shift Booked action handlers
    async handleFirstShiftSpokeTo() {
      if (!(this as any).selectedHygienist) {
        (this as any).showSnackbar("No hygienist selected", "error");
        return;
      }

      const hygienist = (this as any).selectedHygienist;

      try {
        await updateFirstBookedStatus({
          data: {
            JobId: hygienist.JobId,
            HygienistId: hygienist.HygienistId || hygienist.id,
            firstName: hygienist.firstName,
            hygienistEmail: hygienist.hygienistEmail || hygienist.email,
            dentistEmail: hygienist.dentistEmail
          },
          status: 1 // 1 = Spoke To
        });

        (this as any).showSnackbar(
          `Spoke to ${hygienist.firstName} - email sent and status updated`,
          "success"
        );
        await (this as any).fetchHygienists();
      } catch (error) {
        (this as any).showSnackbar(
          error instanceof Error
            ? error.message
            : "Error updating first shift status",
          "error"
        );
      }
    },

    async handleFirstShiftLeftMessage() {
      if (!(this as any).selectedHygienist) {
        (this as any).showSnackbar("No hygienist selected", "error");
        return;
      }

      const hygienist = (this as any).selectedHygienist;

      try {
        await updateFirstBookedStatus({
          data: {
            JobId: hygienist.JobId,
            HygienistId: hygienist.HygienistId || hygienist.id,
            firstName: hygienist.firstName,
            hygienistEmail: hygienist.hygienistEmail || hygienist.email,
            dentistEmail: hygienist.dentistEmail
          },
          status: 2 // 2 = Left Message
        });

        (this as any).showSnackbar(
          `Left message for ${hygienist.firstName} - email sent and status updated`,
          "success"
        );
        await (this as any).fetchHygienists();
      } catch (error) {
        (this as any).showSnackbar(
          error instanceof Error
            ? error.message
            : "Error updating first shift status",
          "error"
        );
      }
    },

    async handleFirstShiftIgnore() {
      if (!(this as any).selectedHygienist) {
        (this as any).showSnackbar("No hygienist selected", "error");
        return;
      }

      const hygienist = (this as any).selectedHygienist;

      if (!hygienist.JobId) {
        (this as any).showSnackbar(
          "No JobId on this row — cannot ignore",
          "error"
        );
        return;
      }

      try {
        await updateTable({
          tablename: "Job",
          columnname: "firstBooked",
          id: hygienist.JobId,
          value: 6,
          prevValue: hygienist.firstBooked
        });

        (this as any).showSnackbar(
          `Ignored ${hygienist.firstName} - removed from this list`,
          "success"
        );
        invalidateStripeStatusRequest(this as any);
        (this as any).selectedHygienist = null;
        await (this as any).fetchHygienists();
      } catch (error) {
        (this as any).showSnackbar(
          error instanceof Error
            ? error.message
            : "Error ignoring first shift booking",
          "error"
        );
      }
    },

    // T2-6368: updateStatusDirectly() was removed. Membership status is owned by
    // Fair Fresh Start and is no longer editable inline from the AP table.

    async updateAccountStatusDirectly(hygienist: Hygienist, newStatus: number) {
      const prevStatus = hygienist.accountStatus;
      const numericPrevStatus = Number(prevStatus);
      const numericNewStatus = Number(newStatus);

      if (
        numericPrevStatus === numericNewStatus ||
        (isNaN(numericPrevStatus) && isNaN(numericNewStatus))
      ) {
        return;
      }

      // Handle reactivation (Deleted -> Active)
      if (numericPrevStatus === -4 && numericNewStatus === 1) {
        try {
          const { adminReactivateUser } = await import("@/api");
          const result = await adminReactivateUser(hygienist.id);

          hygienist.accountStatus = newStatus;
          (this as any).showSnackbar(
            `Account reactivated successfully. Mailchimp: ${
              result.mailChimpSubscribed ? "subscribed" : "not subscribed"
            }`,
            "success"
          );
          (this as any).fetchHygienists();

          if (
            (this as any).selectedHygienist &&
            (this as any).selectedHygienist.id === hygienist.id
          ) {
            (this as any).fetchHygienistDetails(hygienist.id);
          }
        } catch (error) {
          (this as any).showSnackbar(
            error instanceof Error
              ? error.message
              : "Error reactivating account",
            "error"
          );
        }
        return;
      }

      // Only reactivation is supported via this UI
      (this as any).showSnackbar(
        "Only reactivation (Deleted → Active) is supported. To delete an account, the user must do it themselves.",
        "warning"
      );
    },

    // T2-6369: reactivateHygienist() was removed. Reactivating a Talent's
    // membership tier is handled by Fair Fresh Start, not a manual status write.

    // Member Review Actions
    async reviewed(item: any) {
      await (this.$refs.signupReviewDialog as any).open(item);
    },

    async openMemberReviewModal(item: any, action: string, header: string) {
      (this as any).memberReviewModalReason = "";
      (this as any).memberReviewModalTicket = "";
      (this as any).memberReviewModalAction = action;
      (this as any).memberReviewModalSubmitting = false;
      (this as any).memberReviewModalIdempotencyKey = createAdminSourceCorrectionIdempotencyKey(
        `new-member-review-${action}`
      );
      (this as any).showMemberReviewModal = true;

      let email = item.email || "";

      if (!email && item.id) {
        const hygienist = (this as any).hygienists.find(
          (h: any) => h.id === item.id
        );
        if (hygienist && hygienist.email) {
          email = hygienist.email;
        } else {
          try {
            const userData = await getUserByHygienist(item.id);
            if (Array.isArray(userData) && userData.length > 0) {
              email = userData[0].email || "";
            } else if (
              userData &&
              typeof userData === "object" &&
              (userData as any).email
            ) {
              email = (userData as any).email;
            }
          } catch (error) {
            // If fetching fails, continue without email (will show error when trying to send email)
            // eslint-disable-next-line no-console
            console.error("Error fetching user email:", error);
          }
        }
      }

      (this as any).memberReviewModalData = {
        id: item.id,
        firstName: item.firstName || "",
        email: email,
        status: item.status || 0
      };
      (this as any).memberReviewModalHeader = header;

      if (action === "hold") {
        (this as any).memberReviewModalSubmit = (this as any).handleHold;
      } else if (action === "block") {
        (this as any).memberReviewModalSubmit = (this as any).handleBlock;
      }
    },

    // T2-6429: Hold and Block on the New Members Review tab both apply the
    // shared manual Not a Fit outcome (T2-6396) instead of writing legacy
    // Hold/Blocked status values. The clicked action is prefixed into the
    // support note so audit can tell Hold from Block.
    async handleHold() {
      await (this as any).submitMemberReviewNotAFit("Hold");
    },

    async handleBlock() {
      await (this as any).submitMemberReviewNotAFit("Block");
    },

    async submitMemberReviewNotAFit(actionLabel: "Hold" | "Block") {
      if (
        !(this as any).canSubmitMemberReviewNotAFit ||
        (this as any).memberReviewModalSubmitting
      ) {
        return;
      }
      const modalData = (this as any).memberReviewModalData;
      (this as any).memberReviewModalSubmitting = true;
      try {
        await runMemberReviewNotAFitAction({
          actionLabel,
          hygienistId: modalData.id,
          reason: (this as any).memberReviewModalReason,
          ticketReference: (this as any).memberReviewModalTicket,
          reasonCategory: getNewMemberReviewReasonCategory(actionLabel),
          idempotencyKey: (this as any).memberReviewModalIdempotencyKey,
          // The audited Not a Fit transaction also completes the negative
          // new-member decision; it does not approve ownership warnings.
          apply: applyManualNotAFit
        });

        (this as any).markSelectedSignupReviewed(modalData.id);

        (this as any).showMemberReviewModal = false;
        (this as any).memberReviewModalReason = "";
        (this as any).memberReviewModalTicket = "";
        (this as any).memberReviewModalAction = "";
        (this as any).memberReviewModalIdempotencyKey = "";
        await (this as any).fetchHygienists();

        if (
          (this as any).selectedHygienist &&
          (this as any).selectedHygienist.id === modalData.id
        ) {
          (this as any).fetchHygienistDetails(modalData.id);
        }

        (this as any).showSnackbar(
          `${actionLabel} applied — Talent set to Not a Fit.`,
          "success"
        );
      } catch (error) {
        const message = actionableAdminActionError(
          error,
          "Could not apply Not a Fit."
        );
        (this as any).showSnackbar(message, "error");
      } finally {
        (this as any).memberReviewModalSubmitting = false;
      }
    },

    async confirmBlockOffice() {
      if (!(this as any).selectedHygienist || !(this as any).officeIdToBlock) {
        return;
      }

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        const locationId = (this as any).officeIdToBlock;

        // Add to BlockedDentist table (hygienist blocks office)
        await addBlocked(
          {
            locationId: locationId,
            hygienistId: hygienistId
          },
          "BlockedDentist"
        );

        (this as any).showSnackbar(
          `Office ${locationId} blocked for ${
            (this as any).selectedHygienist.firstName
          } ${(this as any).selectedHygienist.lastName}`,
          "success"
        );

        (this as any).blockOfficeDialog = false;
        (this as any).officeIdToBlock = null;
      } catch (error) {
        (this as any).showSnackbar("Error blocking office", "error");
      }
    },

    handleScript() {
      (this as any).showSnackbar("Script functionality coming soon", "info");
    },

    selectHygienistById(hygienistId: number) {
      const hygienist = (this as any).hygienists.find(
        (h: Hygienist) => h.id === hygienistId
      );
      if (hygienist) {
        (this as any).handleRowClick(hygienist);
      }
    },

    getRowClass(item: Hygienist) {
      return getTalentPresentationRowClass(
        item,
        (this as any).selectedHygienist?.id
      );
    },

    // Canonical membership strings are the only presentation input.
    getStatusColor(status: string | null | undefined): string {
      return getFfsStatusColor(status);
    },
    getStatusIcon(status: string | null | undefined): string | null {
      return getFfsStatusIcon(status);
    },
    statusNeedsDarkText(status: string | null | undefined): boolean {
      return ffsStatusNeedsDarkText(status);
    },
    // T2-6361/T2-6362/T2-6366: resolve the table Status pill so it matches the
    // Profile Information chip for the same Talent.
    //  1. A live "On a Break" overlay outranks the FFS tier. The list endpoint
    //     provides `activeOverlayState` per row (getHygienist() subquery), so EVERY
    //     on-break row shows "On a Break" immediately — not only after it's clicked
    //     (no flip). The selected row may also carry a freshly patched membership
    //     overlay (handleRowClick), which we honor so applying/removing a break in
    //     the detail reflects in the row right away.
    //  2. Otherwise the FFS tier (currentStatusCode): Active/Pro/Elite/RL1/.../Provisional.
    //  3. No MembershipManagement row (no currentStatusCode) → null so the pill
    //     renders "Unknown" (matching profileStatusLabel), never a misleading legacy
    //     "Active". The legacy Hygienist.status int is intentionally NOT used here.
    rowStatusCode(item: any): string | null {
      // A freshly patched overlay from handleRowClick (selected row) wins so
      // applying/removing a recovery state reflects immediately.
      const patchedOverlay = this.getMembershipOverlayStatusCode(item?.membership || null);
      if (patchedOverlay) return patchedOverlay;
      // T2-6516: the list endpoint now enriches `activeOverlayState` for ALL
      // active recovery overlays (grandfathering-aware, newest active row), so the
      // row shows Course Required / Restricted Re-entry / On a Break / Not a Fit
      // over the durable tier — matching the profile chip after selection.
      const overlayCode = recoveryOverlayToDisplayCode(item?.activeOverlayState);
      if (overlayCode) return overlayCode;
      return item && item.currentStatusCode ? (item.currentStatusCode as string) : null;
    },
    // T2-6361: label for the table Status pill. Keep the no-FFS-data placeholder
    // ("Unknown") identical to the Profile Information chip (profileStatusLabel) so
    // the row and the detail panel always agree. Scoped to the Talent view — the
    // shared getFfsStatusLabel helper still returns "—" for the other FFS tables.
    rowStatusLabel(item: any): string {
      const code = (this as any).rowStatusCode(item);
      return code ? this.getStatusLabel(code) : "Unknown";
    },

    getMembershipOverlayStatusCode(membership?: TalentMembershipStatus | null): string | null {
      // T2-6516: map every active recovery overlay state (not just on_a_break) to
      // its AP display code so the profile chip label and the patched selected-row
      // status both reflect Course Required / Restricted Re-entry / On a Break /
      // Not a Fit. `membership.overlay` is the grandfathering-aware effectiveOverlay
      // from the API, so this stays consistent with the table enrichment.
      return recoveryOverlayToDisplayCode(membership?.overlay?.state);
    },
    // T2-6363: format a possibly-null FFS metric for display.
    ffsNum(value: number | null | undefined): string {
      if (value === null || value === undefined || isNaN(Number(value))) {
        return "N/A";
      }
      const num = Number(value);
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    },

    // T2-6363/6367: build a compact threshold "ladder" for a score against named
    // rungs ordered high→low. Each rung is annotated with a state:
    //   crossed  — score has reached/passed this rung
    //   next     — the lowest rung still strictly above the score (next target)
    //   unreached— above the score but not the immediate next target
    // Rungs with a null/undefined threshold are dropped so a partial API payload
    // never renders a NaN rung.
    ffsLadderRungs(
      rungs: Array<{ label: string; value: number | null | undefined }>,
      score: number | null | undefined
    ): Array<{ label: string; value: number; state: string }> {
      const valid = rungs
        .filter(
          (r) =>
            r.value !== null &&
            r.value !== undefined &&
            !isNaN(Number(r.value))
        )
        .map((r) => ({ label: r.label, value: Number(r.value) }));
      const hasScore =
        score !== null && score !== undefined && !isNaN(Number(score));
      const s = hasScore ? Number(score) : null;
      const above =
        s !== null ? valid.filter((r) => r.value > s).map((r) => r.value) : [];
      const nextVal = above.length ? Math.min(...above) : null;
      return valid.map((r) => {
        let state = "unreached";
        if (s !== null && r.value <= s) {
          state = "crossed";
        } else if (nextVal !== null && r.value === nextVal) {
          state = "next";
        }
        return { label: r.label, value: r.value, state };
      });
    },
    ffsRungColor(state: string): string {
      if (state === "crossed") return "green lighten-4";
      if (state === "next") return "blue lighten-4";
      return "grey lighten-3";
    },
    ffsRungIcon(state: string): string {
      if (state === "crossed") return "mdi-check";
      if (state === "next") return "mdi-arrow-up-bold";
      return "mdi-circle-small";
    },
    // T2-6363: reliability score ladder for the Show Stats FFS card. Uses the
    // API-provided thresholds (never hard-coded in the AP).
    reliabilityLadder(): Array<{ label: string; value: number; state: string }> {
      const ffs = (this as any).hygienistStats?.fairFreshStart;
      if (!ffs || !ffs.thresholds || !ffs.thresholds.reliability) return [];
      const t = ffs.thresholds.reliability;
      return (this as any).ffsLadderRungs(
        [
          { label: "Elite", value: t.elite },
          { label: "Pro", value: t.pro },
          { label: "Active", value: t.active },
          { label: "RL1", value: t.rl1 },
          { label: "RL2", value: t.rl2 }
        ],
        ffs.reliability ? ffs.reliability.weightedReliability : null
      );
    },
    // T2-6363: feedback score ladder for the Show Stats FFS card.
    feedbackLadder(): Array<{ label: string; value: number; state: string }> {
      const ffs = (this as any).hygienistStats?.fairFreshStart;
      if (!ffs || !ffs.thresholds || !ffs.thresholds.feedback) return [];
      const t = ffs.thresholds.feedback;
      return (this as any).ffsLadderRungs(
        [
          { label: "Elite", value: t.elite },
          { label: "Pro", value: t.pro },
          { label: "Active", value: t.active },
          { label: "FL1", value: t.fl1 },
          { label: "FL2", value: t.fl2 }
        ],
        ffs.feedback ? ffs.feedback.weightedFeedbackScore : null
      );
    },
    // T2-6363: completed-shift milestones (provisional exit / Pro / Elite).
    completedShiftRungs(): Array<{ label: string; value: number; state: string }> {
      const ffs = (this as any).hygienistStats?.fairFreshStart;
      if (!ffs || !ffs.thresholds || !ffs.thresholds.completedShifts) return [];
      const t = ffs.thresholds.completedShifts;
      return (this as any).ffsLadderRungs(
        [
          { label: "Elite", value: t.elite },
          { label: "Pro", value: t.pro },
          { label: "Provisional exit", value: t.provisionalExit }
        ],
        ffs.reliability ? ffs.reliability.completedShifts : null
      );
    },
    // T2-6363: rated-shift milestones (Active / Pro / Elite).
    ratedShiftRungs(): Array<{ label: string; value: number; state: string }> {
      const ffs = (this as any).hygienistStats?.fairFreshStart;
      if (!ffs || !ffs.thresholds || !ffs.thresholds.ratedShifts) return [];
      const t = ffs.thresholds.ratedShifts;
      return (this as any).ffsLadderRungs(
        [
          { label: "Elite", value: t.elite },
          { label: "Pro", value: t.pro },
          { label: "Active", value: t.active }
        ],
        ffs.feedback ? ffs.feedback.ratedShifts : null
      );
    },
    // T2-6367: reliability threshold ladder for the Cancellation Reliability
    // Editor. Bands are fixed per the ticket (Pro/Active/RL1/RL2).
    cancellationReliabilityLadder(): Array<{
      label: string;
      value: number;
      state: string;
    }> {
      return (this as any).ffsLadderRungs(
        [
          { label: "Pro", value: 0.9 },
          { label: "Active", value: 0.8 },
          { label: "RL1", value: 0.65 },
          { label: "RL2", value: 0.45 }
        ],
        (this as any).cancellationWeightedReliability
      );
    },

    formatFairFreshStartStatusCode(statusCode: string | null | undefined): string {
      if (!statusCode) {
        return "N/A";
      }
      const labels: Record<string, string> = {
        Elite: "Elite",
        Pro: "Pro",
        Active: "Active",
        Probation: "Probation",
        CourseRequired: "Course Required",
        RL1: "Reliability Limited 1",
        RL2: "Reliability Limited 2",
        FL1: "Feedback Limited 1",
        FL2: "Feedback Limited 2",
        RL1FL1: "Reliability 1 / Feedback 1",
        RL1FL2: "Reliability 1 / Feedback 2",
        RL2FL1: "Reliability 2 / Feedback 1",
        RL2FL2: "Reliability 2 / Feedback 2"
      };
      return labels[statusCode] || startCase(statusCode.replace(/([a-z])([A-Z])/g, "$1 $2"));
    },

    getFairFreshStartVisibleLabel(fairFreshStart: FairFreshStart | null | undefined): string {
      if (!fairFreshStart) {
        return "N/A";
      }
      const statusCode = fairFreshStart.status?.currentStatusCode;
      return fairFreshStart.status?.displayLabel
        || this.formatFairFreshStartStatusCode(statusCode);
    },

    getFairFreshStartRawLabel(fairFreshStart: FairFreshStart | null | undefined): string {
      if (!fairFreshStart) {
        return "N/A";
      }
      const calculatedStatusCode = fairFreshStart.status?.grandfathering?.calculatedStatusCode
        || fairFreshStart.grandfathering?.calculatedStatusCode;
      return this.formatFairFreshStartStatusCode(
        calculatedStatusCode
        || fairFreshStart.status?.rawStatusCode
        || fairFreshStart.status?.currentStatusCode
      );
    },

    getFairFreshStartProtectedLabel(fairFreshStart: FairFreshStart | null | undefined): string {
      if (!fairFreshStart) {
        return "N/A";
      }
      return this.formatFairFreshStartStatusCode(
        fairFreshStart.status?.grandfathering?.protectedStatusCode
        || fairFreshStart.grandfathering?.protectedStatusCode
        || fairFreshStart.status?.currentStatusCode
      );
    },

    isFairFreshStartProtected(fairFreshStart: FairFreshStart | null | undefined): boolean {
      if (fairFreshStart?.status?.grandfathering?.active) {
        return true;
      }
      if (fairFreshStart?.grandfathering?.isActive) {
        return true;
      }
      if (!fairFreshStart?.grandfathering?.protectedStatusCode || !fairFreshStart.grandfathering.grandfatheredUntil) {
        return false;
      }
      const untilTimestamp = Date.parse(fairFreshStart.grandfathering.grandfatheredUntil);
      return Number.isFinite(untilTimestamp) && untilTimestamp > Date.now();
    },

    getFairFreshStartStatusColor(fairFreshStart: FairFreshStart | null | undefined): string {
      if (this.isFairFreshStartProtected(fairFreshStart)) {
        return "amber darken-2";
      }
      const statusCode = fairFreshStart?.status?.currentStatusCode;
      if (statusCode) {
        return this.getStatusColor(statusCode);
      }
      const label = this.getFairFreshStartVisibleLabel(fairFreshStart).toLowerCase();
      if (label.includes("elite")) {
        return "purple";
      }
      if (label.includes("active")) {
        return "green";
      }
      if (label.includes("course") || label.includes("probation")) {
        return "orange darken-1";
      }
      if (label === "pro") {
        return "#0B4598";
      }
      if (label.includes("limited") || label.includes("not a fit") || label.includes("break")) {
        return "red darken-1";
      }
      return "grey";
    },

    formatFairFreshStartMetric(value: number | null | undefined, digits = 2): string {
      if (value === null || typeof value === "undefined") {
        return "N/A";
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue.toFixed(digits) : "N/A";
    },

    formatFairFreshStartPercentile(
      value: number | null | undefined,
      label: string | null | undefined
    ): string {
      if (value === null || typeof value === "undefined") {
        return label || "N/A";
      }
      return label ? `${value} (${label})` : `${value}`;
    },

    getFairFreshStartCompletedShifts(fairFreshStart: FairFreshStart | null | undefined): string {
      const value = fairFreshStart?.status?.completedShifts ?? fairFreshStart?.reliability?.completedShifts;
      return value === null || typeof value === "undefined" ? "N/A" : String(value);
    },

    getFairFreshStartRatedShifts(fairFreshStart: FairFreshStart | null | undefined): string {
      const value = fairFreshStart?.status?.ratedShifts ?? fairFreshStart?.feedback?.ratedShifts;
      return value === null || typeof value === "undefined" ? "N/A" : String(value);
    },

    getFairFreshStartFeedbackCount(fairFreshStart: FairFreshStart | null | undefined): string {
      const value = fairFreshStart?.feedback?.feedbackCount
        ?? fairFreshStart?.feedback?.ratedShifts
        ?? fairFreshStart?.status?.ratedShifts;
      return value === null || typeof value === "undefined" ? "N/A" : String(value);
    },

    getValueGivesColor(valueGives: number | null | undefined): string {
      if (valueGives === null || valueGives === undefined) {
        return "grey";
      }
      if (valueGives >= 0) {
        return "green";
      }
      if (valueGives >= -3) {
        return "orange";
      }
      if (valueGives >= -8) {
        return "red darken-1";
      }
      // Below -8 (approaching revoke threshold)
      return "red darken-4";
    },

    getValueGivesWarning(valueGives: number | null | undefined): string {
      if (valueGives === null || valueGives === undefined) {
        return "";
      }
      if (valueGives <= -10) {
        return "⚠️ At revoke threshold!";
      }
      if (valueGives <= -8) {
        return "⚠️ Near revoke threshold";
      }
      if (valueGives < 0) {
        return "⚠️ Negative score";
      }
      return "";
    },

    getCancellationColor(percentage: number | null | undefined): string {
      if (percentage === null || percentage === undefined) {
        return "grey";
      }
      if (percentage <= 5) {
        return "#4CAF50"; // Green - excellent
      }
      if (percentage <= 10) {
        return "#8BC34A"; // Light green - good
      }
      if (percentage <= 15) {
        return "#FF9800"; // Orange - warning
      }
      if (percentage <= 25) {
        return "#FF5722"; // Deep orange - concerning
      }
      return "#F44336"; // Red - high cancellation rate
    },

    getCancellationRowClass(item: Cancellation): string {
      if (item.adminMode === "ignored") {
        return "cancellation-ignored";
      }
      if (item.adminMode === "grouped") {
        return "cancellation-grouped";
      }
      if (item.isGracePeriodCancellation) {
        return "cancellation-grace-period";
      }
      if (!this.isCancellationIncludedInReliability(item)) {
        return "cancellation-before-clean-slate";
      }
      return "";
    },

    async handleShowStats() {
      if (!(this as any).selectedHygienist) {
        (this as any).showSnackbar("Please select a hygienist first", "warning");
        return;
      }

      (this as any).loadingStats = true;
      (this as any).showStatsDialog = true;
      (this as any).hygienistStats = null;

      try {
        const stats = await getHygienistStats((this as any).selectedHygienist.id);
        (this as any).hygienistStats = stats;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Error fetching stats";
        (this as any).showSnackbar(message, "error");
      } finally {
        (this as any).loadingStats = false;
      }
    },

    /**
     * Calculate estimated penalty for a cancellation based on shift date and cancel timestamp
     * This mirrors the backend logic for display purposes
     */
    // T2-6371: calculateEstimatedPenalty() was removed with the dead
    // ignore/restore cancellation dialogs that were its only callers.

    getMemberReviewStatusColor(status: number): string {
      // Bold, vibrant colors for member review status pills
      const statusColors: Record<number, string> = {
        "-4": "#9E9E9E", // Deactivated - medium grey
        "-3": "#FF9800", // Revoked - orange
        "-2": "#F44336", // Blacklisted - red
        "-1": "#FFC107", // Probation - amber/yellow
        0: "#4CAF50", // Active - green
        1: "#2196F3", // Pro - blue
        2: "#9C27B0" // Elite - purple
      };
      return statusColors[status] || "#9E9E9E";
    },

    getMemberReviewStatusDark(status: number): boolean {
      // Return true for dark backgrounds so text is white
      // Yellow/amber needs dark text for readability
      return status !== -1;
    },

    getRoleLabel(type: number | null): string {
      const roleLabels: Record<number, string> = {
        0: "Hygienist",
        1: "Dental Assistant",
        2: "Admin / Front Desk",
        3: "Associate Dentist"
      };
      return type !== null && roleLabels[type] ? roleLabels[type] : "Unknown";
    },

    getStatusLabel(status: string | null | undefined): string {
      return getFfsStatusLabel(status);
    },

    isAccountDeleted(status: number | null | undefined): boolean {
      if (status === null || typeof status === "undefined") {
        return false;
      }
      return Number(status) === -4;
    },

    getJobStatusColor(status: number): string {
      return JOB_STATUS_COLORS[Number(status)] || "grey";
    },

    getJobStatusLabel(status: number): string {
      return formatJobStatus(status);
    },

    formatDate(dateString: string): string {
      if (!dateString) return "N/A";
      const str = typeof dateString === "string" ? dateString.trim() : String(dateString).trim();
      // Extract the YYYY-MM-DD portion and parse as local midnight
      // This prevents timezone shift (e.g. 2018-05-14T00:00:00.000Z becoming May 13 in EST)
      const dateMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const date = new Date(dateMatch[1] + "T00:00:00");
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric"
          });
        }
      }
      const date = new Date(str);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    formatBlockedUntilDate(value: string | null | undefined): string {
      if (!value || value === null || value === "") {
        return "—";
      }
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return "—";
        }
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch (error) {
        return "—";
      }
    },

    openBlockedUntilEditor(hygienist: Hygienist): void {
      const date = hygienist.blockedUntil ? new Date(hygienist.blockedUntil) : null;
      const pad = (value: number) => String(value).padStart(2, "0");
      const dateTimeLocal = date && !Number.isNaN(date.getTime())
        ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        : "";
      (this as any).blockedUntilEditor = {
        show: true,
        hygienist,
        dateTimeLocal,
        reason: "",
        ticketReference: "",
        saving: false,
        idempotencyKey: "",
        requestPayload: ""
      };
    },

    async submitBlockedUntil(clearDate: boolean): Promise<void> {
      const editor = (this as any).blockedUntilEditor;
      if (!editor.hygienist || editor.saving) return;
      let blockedUntil: string | null = null;
      if (!clearDate) {
        blockedUntil = localBlockedUntilToUtc(editor.dateTimeLocal);
        if (!blockedUntil) {
          (this as any).showSnackbar("Enter a valid Blocked Until date and time.", "error");
          return;
        }
      }
      const payload = {
        blockedUntil,
        reason: editor.reason.trim(),
        ticketReference: editor.ticketReference.trim()
      };
      if (!payload.reason || !payload.ticketReference) return;
      const retry = blockedUntilRetryState(
        editor.requestPayload,
        editor.idempotencyKey,
        payload,
        () => createAdminSourceCorrectionIdempotencyKey("blocked-until")
      );
      editor.idempotencyKey = retry.idempotencyKey;
      editor.requestPayload = retry.requestPayload;
      editor.saving = true;
      const hygienistId = editor.hygienist.id;
      const isCurrentTalent = () =>
        (this as any).selectedHygienist?.id === hygienistId;
      try {
        const refreshed = await saveAndRefreshBlockedUntil(
          () => updateTalentBlockedUntil(hygienistId, payload, editor.idempotencyKey),
          () => (this as any).fetchHygienists(true),
          async () => {
            if (!isCurrentTalent()) return true;
            const detailsRefreshed = await (this as any).fetchHygienistDetails(hygienistId, {
              shouldCommit: isCurrentTalent,
              throwOnError: true,
              refreshOpenPanels: false
            });
            return !isCurrentTalent() || detailsRefreshed;
          }
        );
        editor.show = false;
        (this as any).showSnackbar(
          !refreshed
            ? "Blocked Until saved, but the Talent view could not refresh. Reload to see the latest value."
            : "Blocked Until updated.",
          refreshed ? "success" : "warning"
        );
      } catch (error) {
        (this as any).showSnackbar(
          error instanceof Error ? error.message : "Could not update Blocked Until.",
          "error"
        );
      } finally {
        editor.saving = false;
      }
    },

    formatDateTime(value: string | null | undefined): string {
      if (!value) {
        return "N/A";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "N/A";
      }
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    refreshHygienistData() {
      (this as any).fetchHygienists();
    },

    formatCountryName(countryId: number | null | undefined): string {
      if (countryId === 0) {
        return "Canada";
      }
      if (countryId === 1) {
        return "United States";
      }
      return "";
    },

    calculateYearsExperience(
      graduationYear: number | null | undefined
    ): number {
      if (!graduationYear) return 0;
      const currentYear = new Date().getFullYear();
      return Math.max(0, currentYear - graduationYear);
    },

    // T2-6362: cycle the Status column sort (none -> tier DESC -> tier ASC ->
    // none). The custom #header.status slot removes Vuetify's native sort
    // toggle, so we drive tableOptions ourselves; the deep watcher then
    // refetches and the API applies the FFS tier-rank ORDER BY.
    toggleStatusSort() {
      const opts: DataTableOptions = (this as any).tableOptions || {};
      const active =
        Array.isArray(opts.sortBy) && opts.sortBy.indexOf("status") !== -1;
      const desc =
        Array.isArray(opts.sortDesc) && opts.sortDesc.length
          ? Boolean(opts.sortDesc[0])
          : true;

      let nextSortBy: string[] = [];
      let nextSortDesc: boolean[] = [];
      if (!active) {
        // First click: best tier first (Elite -> ... -> Provisional).
        nextSortBy = ["status"];
        nextSortDesc = [true];
      } else if (desc) {
        // Second click: worst tier first.
        nextSortBy = ["status"];
        nextSortDesc = [false];
      } else {
        // Third click: clear the sort, back to default ordering.
        nextSortBy = [];
        nextSortDesc = [];
      }

      (this as any).tableOptions = {
        ...opts,
        page: 1,
        sortBy: nextSortBy,
        sortDesc: nextSortDesc
      };
    },

    showSnackbar(text: string, color: string) {
      (this as any).snackbar = {
        show: true,
        text,
        color
      };
    },
    // Duplicate-hold review: evidence chips recorded by server-side detection.
    duplicateSignalChips(item: any): string[] {
      let signals = item && item.signals;
      if (typeof signals === "string") {
        try {
          signals = JSON.parse(signals);
        } catch {
          signals = null;
        }
      }
      if (!signals) return [];
      const chips: string[] = [];
      if (signals.phoneMatch) chips.push("phone");
      if (signals.cdhoMatch) chips.push("CDHO");
      if (signals.postalCodeMatch) chips.push("postal");
      return chips;
    },
    async resolveDuplicatePair(item: any, action: "confirm" | "clear") {
      (this as any).resolvingDuplicateId = item.id;
      try {
        const result = await resolveDuplicate(item.id, action);
        item.status = (result && result.status) || (action === "confirm" ? "confirmed" : "cleared");
        item.resolvedAt = new Date().toISOString();
        const tier = result && result.holdState ? result.holdState.tier : null;
        const suffix =
          action === "clear"
            ? tier === "hard"
              ? " — other pending matches still hold this account"
              : " — hold released, talent can view and book again"
            : " — hold stays in place";
        (this as any).showSnackbar(
          `Duplicate pair ${action === "confirm" ? "confirmed" : "cleared"}${suffix}`,
          action === "confirm" ? "orange" : "success"
        );
      } catch (error) {
        (this as any).showSnackbar("Failed to resolve duplicate pair", "error");
      } finally {
        (this as any).resolvingDuplicateId = null;
      }
    },
    updateTalentHeaders(headers: TableHeader[]) {
      (this as any).managedTalentHeaders = headers;
    },
    truncateNotes(notes: string | null | undefined): string {
      if (!notes) {
        return "-";
      }
      const notesStr = String(notes).trim();
      if (
        notesStr === "" ||
        notesStr === "-" ||
        notesStr.toLowerCase() === "n/a"
      ) {
        return "-";
      }
      // Truncate to 60 characters for table display
      if (notesStr.length <= 60) {
        return notesStr;
      }
      return notesStr.substring(0, 60) + "...";
    },
    shouldShowNotesButton(notes: string | null | undefined): boolean {
      if (!notes) {
        return false;
      }
      const notesStr = String(notes).trim();
      if (
        notesStr === "" ||
        notesStr === "-" ||
        notesStr.toLowerCase() === "n/a"
      ) {
        return false;
      }
      // Show button if notes are longer than 60 characters or have line breaks
      if (notesStr.length > 60) {
        return true;
      }
      const lineBreakCount = (notesStr.match(/\n|\r\n|\r/g) || []).length;
      if (lineBreakCount >= 2) {
        return true;
      }
      // Show button if there's at least one line break and content is substantial
      if (lineBreakCount >= 1 && notesStr.length > 40) {
        return true;
      }
      return false;
    },
    openNotesDialog(
      notes: string | null | undefined,
      hygienistId: number | null = null
    ) {
      (this as any).notesDialog = {
        show: true,
        content: notes || "",
        editContent: notes || "",
        isEditing: false,
        isSaving: false,
        hygienistId: hygienistId
      };
    },
    // T2-6230: Allow creating the first note (and editing) directly from the
    // Talent board, even when the talent has zero existing notes. Pre-fills
    // the composer with any existing Hygienist.Notes so saving never wipes
    // prior content (setHygienistNotes overwrites the whole field).
    openAddNoteDialog() {
      const selected = (this as any).selectedHygienist;
      if (!selected || !selected.id) {
        return;
      }
      const existing = (this as any).hygienistNotes || "";
      (this as any).notesDialog = {
        show: true,
        content: existing,
        editContent: existing,
        isEditing: true,
        isSaving: false,
        hygienistId: selected.id
      };
    },
    async saveNotesDialog() {
      const dialog = (this as any).notesDialog;
      if (!dialog.hygienistId) {
        (this as any).snackbar = {
          show: true,
          text: "Cannot save: No hygienist selected",
          color: "error"
        };
        return;
      }

      dialog.isSaving = true;
      try {
        await setHygienistNotes({
          id: dialog.hygienistId,
          notes: dialog.editContent
        });

        dialog.content = dialog.editContent;
        dialog.isEditing = false;

        const hygienistInList = (this as any).hygienists.find(
          (h: Hygienist) => h.id === dialog.hygienistId
        );
        if (hygienistInList) {
          hygienistInList.Notes = dialog.editContent;
        }

        if ((this as any).selectedHygienist?.id === dialog.hygienistId) {
          (this as any).selectedHygienist.Notes = dialog.editContent;
        }

        (this as any).snackbar = {
          show: true,
          text: "Notes saved successfully",
          color: "success"
        };
      } catch (error) {
        (this as any).snackbar = {
          show: true,
          text: "Failed to save notes",
          color: "error"
        };
      } finally {
        dialog.isSaving = false;
      }
    },
    cancelNotesEdit() {
      const dialog = (this as any).notesDialog;
      dialog.editContent = dialog.content;
      dialog.isEditing = false;
    },
    hasUnsavedNotesChanges(): boolean {
      const dialog = (this as any).notesDialog;
      return dialog.editContent !== dialog.content;
    },
    handleCancelNotesEdit() {
      if (this.hasUnsavedNotesChanges()) {
        (this as any).unsavedChangesDialog = {
          show: true,
          action: "cancel"
        };
      } else {
        this.cancelNotesEdit();
      }
    },
    handleCloseNotesDialog() {
      const dialog = (this as any).notesDialog;
      if (dialog.isEditing && this.hasUnsavedNotesChanges()) {
        (this as any).unsavedChangesDialog = {
          show: true,
          action: "close"
        };
      } else {
        dialog.show = false;
      }
    },
    confirmDiscardChanges() {
      const action = (this as any).unsavedChangesDialog.action;
      (this as any).unsavedChangesDialog.show = false;

      if (action === "cancel") {
        this.cancelNotesEdit();
      } else if (action === "close") {
        const dialog = (this as any).notesDialog;
        dialog.editContent = dialog.content;
        dialog.isEditing = false;
        dialog.show = false;
      }

      (this as any).unsavedChangesDialog.action = null;
    },
    // T2-6371: the ignore/restore-cancellation dialog methods
    // (openIgnoreCancellationDialog / closeIgnoreCancellationDialog /
    // confirmIgnoreCancellation / openRestoreCancellationDialog /
    // closeRestoreCancellationDialog / confirmRestoreCancellation /
    // moveCancellationToIgnored / moveCancellationToActive) were removed. They
    // were never invoked from the template; ignore/restore is now done inline in
    // the Cancellations Overview editor via the per-row mode selector + save.

    /**
     * Clears the Stripe Account ID for the selected hygienist
     */
    async clearStripeAccountId() {
      if (!(this as any).selectedHygienist) return;

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        await setFieldToNULL({
          tablename: "Hygienist",
          columnname: "stripeAccountId",
          id: hygienistId
        });

        // Update local state
        (this as any).selectedHygienist.stripeAccountId = null;

        // Update in hygienists list
        const hygienistInList = (this as any).hygienists.find(
          (h: Hygienist) => h.id === hygienistId
        );
        if (hygienistInList) {
          hygienistInList.stripeAccountId = null;
        }

        (this as any).showSnackbar("Stripe Account ID cleared successfully", "success");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        (this as any).showSnackbar(`Error clearing Stripe Account ID: ${message}`, "error");
      }
    },

    /**
     * Clears the Stripe Created Timestamp for the selected hygienist
     */
    async clearStripeCreatedTimestamp() {
      if (!(this as any).selectedHygienist) return;

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        await setFieldToNULL({
          tablename: "Hygienist",
          columnname: "stripeCreatedTimestamp",
          id: hygienistId
        });

        // Update local state
        (this as any).selectedHygienist.stripeCreatedTimestamp = null;

        // Update in hygienists list
        const hygienistInList = (this as any).hygienists.find(
          (h: Hygienist) => h.id === hygienistId
        );
        if (hygienistInList) {
          hygienistInList.stripeCreatedTimestamp = null;
        }

        (this as any).showSnackbar("Stripe Created Timestamp cleared successfully", "success");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        (this as any).showSnackbar(`Error clearing Stripe Created Timestamp: ${message}`, "error");
      }
    },

    /**
     * Saves the school name for the selected hygienist
     */
    async saveSchool() {
      if (!(this as any).selectedHygienist) return;

      try {
        const hygienistId = (this as any).selectedHygienist.id;
        const prevValue = (this as any).selectedHygienist.school || "";
        const newValue = (this as any).editableSchool;

        await updateTable({
          tablename: "Hygienist",
          columnname: "school",
          id: hygienistId,
          value: newValue,
          prevValue: prevValue
        });

        // Update local state
        (this as any).selectedHygienist.school = newValue;

        // Update in hygienists list
        const hygienistInList = (this as any).hygienists.find(
          (h: Hygienist) => h.id === hygienistId
        );
        if (hygienistInList) {
          hygienistInList.school = newValue;
        }

        (this as any).showSnackbar("School updated successfully", "success");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        (this as any).showSnackbar(`Error updating school: ${message}`, "error");
      }
    },

    // T2-6513 / T2-6706: open Qualifications editor and load province-specific options.
    async startDaLevelEdit() {
      if (!(this as any).selectedHygienist || !(this as any).isDentalAssistantProfile) {
        return;
      }
      if ((this as any).savingDaLevel) {
        return;
      }
      (this as any).editingDaLevel = true;
      (this as any).loadingDaLevelOptions = true;
      (this as any).daLevelEditError = "";
      (this as any).daLevelOptions = [];
      (this as any).editableDaLevelSkillId = null;
      (this as any).daLevelCurrentSkillId = null;
      (this as any).daLevelConflictNames = [];
      try {
        const hygienistId = (this as any).selectedHygienist.id;
        const data = await getHygienistQualificationOptions(hygienistId);
        // Guard against stale response after switching Talent accounts.
        if ((this as any).selectedHygienist?.id !== hygienistId) {
          return;
        }
        (this as any).daLevelOptions = data.options || [];
        (this as any).daLevelCurrentSkillId = data.currentSkillId;
        (this as any).editableDaLevelSkillId = data.currentSkillId;
        // T2-7535: with two designations saved there is no current one, so
        // nothing is preselected and saving either choice is allowed.
        (this as any).daLevelConflictNames = data.hasConflict ? (data.currentSkillNames || []) : [];
        if (!(this as any).daLevelOptions.length) {
          (this as any).daLevelEditError =
            "No qualification options are available for this Talent's role and location.";
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        (this as any).editingDaLevel = false;
        (this as any).daLevelEditError = "";
        (this as any).showSnackbar(
          `Failed to load qualification options: ${message}`,
          "error"
        );
      } finally {
        (this as any).loadingDaLevelOptions = false;
      }
    },

    cancelDaLevelEdit() {
      (this as any).editingDaLevel = false;
      (this as any).editableDaLevelSkillId = null;
      (this as any).daLevelCurrentSkillId = null;
      (this as any).daLevelConflictNames = [];
      (this as any).daLevelOptions = [];
      (this as any).savingDaLevel = false;
      (this as any).loadingDaLevelOptions = false;
      (this as any).daLevelEditError = "";
    },

    async saveDaLevel() {
      if (!(this as any).selectedHygienist || !(this as any).isDentalAssistantProfile) {
        return;
      }
      if ((this as any).savingDaLevel) {
        return;
      }
      const skillId = Number((this as any).editableDaLevelSkillId);
      if (!Number.isInteger(skillId) || skillId <= 0) {
        (this as any).daLevelEditError = "Select a qualification before saving";
        (this as any).showSnackbar("Select a qualification before saving", "error");
        return;
      }
      if (skillId === (this as any).daLevelCurrentSkillId) {
        (this as any).cancelDaLevelEdit();
        return;
      }

      const hygienistId = (this as any).selectedHygienist.id;
      const previousQualifications = [
        ...((this as any).profileInfo?.qualifications || [])
      ];
      const previousLevel = (this as any).selectedHygienist?.level;

      (this as any).savingDaLevel = true;
      (this as any).daLevelEditError = "";
      try {
        const result = await updateHygienistQualificationLevel(hygienistId, skillId);
        // Do not apply a successful response for a different Talent than currently selected.
        if ((this as any).selectedHygienist?.id !== hygienistId) {
          return;
        }
        const selectedOption = ((this as any).daLevelOptions || []).find(
          (o: { id: number }) => Number(o.id) === skillId
        );
        const levelName = result.newSkillName || selectedOption?.name || String(skillId);
        const levelLabel =
          result.newSkillLabel || selectedOption?.label || levelName;

        // Refresh profile chips + table Level column only after confirmed save.
        if ((this as any).profileInfo) {
          (this as any).profileInfo.qualifications = [levelLabel];
        }
        const hygienistInList = (this as any).hygienists.find(
          (h: Hygienist) => h.id === hygienistId
        );
        if (hygienistInList) {
          hygienistInList.level = levelName;
        }
        if ((this as any).selectedHygienist) {
          (this as any).selectedHygienist.level = levelName;
        }

        (this as any).cancelDaLevelEdit();
        (this as any).showSnackbar(
          `Qualification updated to ${levelName} (logged in Notes).`,
          "success"
        );

        // Soft-refresh profile so skills/notes reflect the change.
        try {
          const refreshed = await getHygienistProfile(hygienistId);
          if (
            refreshed &&
            (this as any).profileInfo &&
            (this as any).selectedHygienist?.id === hygienistId
          ) {
            (this as any).profileInfo.qualifications =
              refreshed.qualifications || [levelLabel];
            (this as any).profileInfo.skills = refreshed.skills || (this as any).profileInfo.skills;
          }
        } catch (_refreshErr) {
          // Local state already updated; refresh is best-effort.
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        // Restore last confirmed display — no optimistic persist on failure.
        if ((this as any).profileInfo) {
          (this as any).profileInfo.qualifications = previousQualifications;
        }
        if ((this as any).selectedHygienist) {
          (this as any).selectedHygienist.level = previousLevel;
        }
        (this as any).daLevelEditError = message;
        (this as any).showSnackbar(`Error updating qualification: ${message}`, "error");
      } finally {
        (this as any).savingDaLevel = false;
      }
    },

    /**
     * Toggles the collapse state of a section
     */
    toggleSection(section: 'notes' | 'jobs' | 'invoices' | 'duplicates' | 'payments' | 'feedback' | 'jobRatings' | 'courses' | 'blocksFavourites') {
      (this as any).collapsedSections[section] = !(this as any).collapsedSections[section];
      // Lazy-load feedback when first opened
      if (section === 'feedback' && !(this as any).collapsedSections.feedback) {
        if ((this as any).feedback.length === 0 && (this as any).selectedHygienist) {
          (this as any).fetchFeedback((this as any).selectedHygienist.id);
        }
      }
      // T2-6478: lazy-load Job Rating rows when first opened.
      if (section === 'jobRatings' && !(this as any).collapsedSections.jobRatings) {
        if ((this as any).jobRatings.length === 0 && (this as any).selectedHygienist) {
          (this as any).fetchJobRatings((this as any).selectedHygienist.id);
        }
      }
      // T2-6397: lazy-load courses when first opened.
      if (section === 'courses' && !(this as any).collapsedSections.courses) {
        if ((this as any).selectedHygienist) {
          (this as any).fetchCourses((this as any).selectedHygienist.id);
        }
      }
    },

    // T2-6397: fetch the read-only Courses list (required/recommended + live
    // TalentLMS progress) for the Talent profile.
    async fetchCourses(hygienistId: number) {
      const requestedId = Number(hygienistId);
      const result = await loadCoursesForSelectedTalent(
        this as any,
        requestedId,
        () => Number((this as any).selectedHygienist?.id),
        () => getHygienistCourses(requestedId)
      );
      if (result.state === "error") {
        (this as any).showSnackbar(
          result.error instanceof Error
            ? result.error.message
            : "Error fetching courses",
          "error"
        );
      }
    },

    async loadCourseResetPermission() {
      (this as any).loadingCourseResetPermission = true;
      try {
        const currentAdmin = await getCurrentAdminRole();
        (this as any).currentAdminRole = currentAdmin.role;
      } catch {
        // Fail closed: the API independently enforces the same role gate.
        (this as any).currentAdminRole = null;
      } finally {
        (this as any).loadingCourseResetPermission = false;
      }
    },

    canResetCourseProgress(course: HygienistCourse): boolean {
      if ((this as any).loadingCourseResetPermission) return false;
      return canResetCourseProgressForRole(
        (this as any).currentAdminRole,
        course
      );
    },

    formatCourseCompletionDate(completedOn: string | null): string {
      return formatCourseCompletionDateForDisplay(completedOn);
    },

    openCourseResetDialog(course: HygienistCourse) {
      if (!(this as any).canResetCourseProgress(course)) return;
      const hygienistId = Number((this as any).selectedHygienist?.id);
      if (!hygienistId || !(this as any).courses.includes(course)) return;
      (this as any).courseResetDialog = { open: true, course, hygienistId };
    },

    closeCourseResetDialog() {
      if ((this as any).resettingCourseCode) return;
      (this as any).courseResetDialog = {
        open: false,
        course: null,
        hygienistId: null
      };
    },

    async confirmCourseProgressReset() {
      const course = (this as any).courseResetDialog.course as HygienistCourse | null;
      const hygienistId = Number((this as any).courseResetDialog.hygienistId);
      const selectedHygienistId = Number((this as any).selectedHygienist?.id);
      if (!course?.code || !hygienistId
        || !isCurrentCourseTalent(selectedHygienistId, hygienistId)
        || !(this as any).courses.includes(course)
        || !(this as any).canResetCourseProgress(course)) {
        (this as any).courseResetDialog = {
          open: false,
          course: null,
          hygienistId: null
        };
        (this as any).showSnackbar("Course progress cannot be reset.", "error");
        return;
      }

      (this as any).resettingCourseCode = course.code;
      const resetRequestId = ++(this as any).courseResetRequestId;
      try {
        await resetHygienistCourseProgress(hygienistId, course.code);
        if (resetRequestId !== (this as any).courseResetRequestId
          || !isCurrentCourseTalent(
            Number((this as any).selectedHygienist?.id),
            hygienistId
          )) return;
        await (this as any).fetchCourses(hygienistId);
        if (resetRequestId !== (this as any).courseResetRequestId
          || !isCurrentCourseTalent(
            Number((this as any).selectedHygienist?.id),
            hygienistId
          )) return;
        (this as any).courseResetDialog = {
          open: false,
          course: null,
          hygienistId: null
        };
        (this as any).showSnackbar(
          `${course.name} is now Not started and ready to retake.`,
          "success"
        );
      } catch (error) {
        if (resetRequestId !== (this as any).courseResetRequestId
          || !isCurrentCourseTalent(
            Number((this as any).selectedHygienist?.id),
            hygienistId
          )) return;
        (this as any).showSnackbar(
          error instanceof Error ? error.message : "Could not reset course progress.",
          "error"
        );
      } finally {
        if (resetRequestId === (this as any).courseResetRequestId) {
          (this as any).resettingCourseCode = "";
        }
      }
    },

    // T2-6397: presentation helpers for the Courses section.
    courseStatusColor(status: string): string {
      switch (status) {
        case "passed":
        case "completed":
          return "green";
        case "in_progress":
          return "#0B4598";
        default:
          return "grey";
      }
    },
    courseStatusLabel(status: string): string {
      switch (status) {
        case "passed":
          return "Passed";
        case "completed":
          return "Completed";
        case "in_progress":
          return "In progress";
        case "not_started":
          return "Not started";
        default:
          return status || "Unknown";
      }
    },

    async fetchFeedback(
      hygienistId: number,
      options: {
        shouldCommit?: () => boolean;
        throwOnError?: boolean;
      } = {}
    ): Promise<boolean> {
      if ((this as any).feedbackAbortController) {
        (this as any).feedbackAbortController.abort();
      }
      (this as any).feedbackAbortController = new AbortController();
      const signal = (this as any).feedbackAbortController.signal;
      (this as any).loadingFeedback = true;
      try {
        const opts = (this as any).feedbackOptions;
        const sortBy = opts.sortBy && opts.sortBy.length ? opts.sortBy[0] : 'shiftDate';
        const sortDesc = opts.sortDesc && opts.sortDesc.length ? opts.sortDesc[0] : true;
        const result = await getHygienistFeedback(
          hygienistId,
          opts.page || 1,
          opts.itemsPerPage || 5,
          sortBy,
          sortDesc,
          signal
        );
        if (options.shouldCommit && !options.shouldCommit()) {
          return false;
        }
        (this as any).feedback = result.data;
        (this as any).feedbackTotal = result.total;
        return true;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return false;
        if (options.throwOnError) {
          throw error;
        }
        if (options.shouldCommit && !options.shouldCommit()) {
          return false;
        }
        (this as any).feedback = [];
        (this as any).feedbackTotal = 0;
        return false;
      } finally {
        if ((this as any).feedbackAbortController?.signal === signal) {
          (this as any).loadingFeedback = false;
        }
      }
    },

    onFeedbackOptionsChange() {
      if ((this as any).selectedHygienist && !(this as any).collapsedSections.feedback) {
        (this as any).fetchFeedback((this as any).selectedHygienist.id);
      }
    },

    openFeedbackText(item: any) {
      (this as any).feedbackFullTextContent = item;
      (this as any).feedbackFullTextDialog = true;
    },

    // T2-6478: Job Rating section — load completed jobs newest-first by Start Date.
    async fetchJobRatings(
      hygienistId: number,
      options: {
        shouldCommit?: () => boolean;
        throwOnError?: boolean;
      } = {}
    ): Promise<boolean> {
      if ((this as any).jobRatingsAbortController) {
        (this as any).jobRatingsAbortController.abort();
      }
      (this as any).jobRatingsAbortController = new AbortController();
      const signal = (this as any).jobRatingsAbortController.signal;
      (this as any).loadingJobRatings = true;
      try {
        const opts = (this as any).jobRatingsOptions;
        const result = await getHygienistJobRatings(
          hygienistId,
          opts.page || 1,
          opts.itemsPerPage || 5,
          signal
        );
        if (options.shouldCommit && !options.shouldCommit()) {
          return false;
        }
        (this as any).jobRatings = result.data;
        (this as any).jobRatingsTotal = result.total;
        return true;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return false;
        if (options.throwOnError) {
          throw error;
        }
        if (options.shouldCommit && !options.shouldCommit()) {
          return false;
        }
        (this as any).jobRatings = [];
        (this as any).jobRatingsTotal = 0;
        return false;
      } finally {
        if ((this as any).jobRatingsAbortController?.signal === signal) {
          (this as any).loadingJobRatings = false;
        }
      }
    },

    onJobRatingsOptionsChange() {
      if ((this as any).selectedHygienist && !(this as any).collapsedSections.jobRatings) {
        (this as any).fetchJobRatings((this as any).selectedHygienist.id);
      }
    },

    openRatingCorrection(sourceType: "feedback" | "job", item: any, field: string) {
      (this as any).ratingCorrectionDialog = {
        open: true,
        sourceType,
        item,
        field,
        value: item?.[field] == null ? null : Number(item[field]),
        reason: "",
        evidenceReference: "",
        idempotencyKey: createAdminSourceCorrectionIdempotencyKey(
          `${sourceType}-${sourceType === "feedback" ? item.id : item.jobId}-${field}`
        ),
        submitting: false
      };
    },

    closeRatingCorrection() {
      if ((this as any).ratingCorrectionDialog.submitting) return;
      (this as any).ratingCorrectionDialog.open = false;
    },

    async confirmRatingCorrection() {
      if (!(this as any).canSubmitRatingCorrection) return;
      const dialog = (this as any).ratingCorrectionDialog;
      const hygienistId = Number((this as any).selectedHygienist?.id);
      dialog.submitting = true;
      try {
        const reconciliationRequestId = hygienistId
          ? (this as any).beginTalentCorrectionReconciliation()
          : 0;
        const result = await runRatingSourceCorrectionAction({
          sourceType: dialog.sourceType,
          sourceId: Number(
            dialog.sourceType === "feedback" ? dialog.item.id : dialog.item.jobId
          ),
          field: dialog.field,
          value: Number(dialog.value),
          reason: String(dialog.reason || ""),
          evidenceReference: String(dialog.evidenceReference || ""),
          idempotencyKey: dialog.idempotencyKey,
          updateFeedback: updateFeedbackRating,
          updateJob: updateJobRating
        });

        dialog.open = false;
        if (hygienistId) {
          const shouldContinue = () =>
            !(this as any)._isDestroyed &&
            !(this as any)._isBeingDestroyed &&
            Number((this as any).selectedHygienist?.id) === hygienistId &&
            Number((this as any).membershipReconciliationRequestId) ===
              reconciliationRequestId;
          const refreshStatus = await refreshRatingCorrectionViews({
            refreshSource: () =>
              dialog.sourceType === "feedback"
                ? (this as any).fetchFeedback(hygienistId, { shouldCommit: shouldContinue })
                : (this as any).fetchJobRatings(hygienistId, { shouldCommit: shouldContinue }),
            refreshTalent: () =>
              (this as any).refreshTalentCorrectionViews(
                hygienistId,
                reconciliationRequestId
              )
          });
          if (refreshStatus === "source_failed") {
            if (shouldContinue()) {
              (this as any).showSnackbar(
                "Correction saved, but the Dashboard could not refresh the rating source panel. Refresh Talent before making another correction.",
                "warning"
              );
            }
            return;
          }
          if (refreshStatus !== "refreshed") return;
        }
        (this as any).showSnackbar(
          result?.success
            ? "Rating source corrected and FFS recalculated"
            : "Rating source corrected; authoritative FFS recomputation is queued for retry",
          result?.success ? "success" : "warning"
        );
      } catch (e) {
        // Keep the dialog and its idempotency key so a retry cannot duplicate
        // a correction whose committed response was lost in transit.
        (this as any).showSnackbar(
          actionableAdminActionError(e, "Failed to correct rating source"),
          "error"
        );
      } finally {
        dialog.submitting = false;
      }
    }
  }
});
